import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

class AgentOverlayComponent extends DialogComponent {
  requiredRefs = [
    'dialog',
    'messages',
    'chatForm',
    'chatInput',
    'chatMic',
    'products',
    'productsScrim',
    'productsPill',
    'productsPillLabel',
    'comparePill',
    'narrowPill',
    'productsFilterSummary',
    'productsCarousel',
    'productsLoading',
    'launcher',
    'panes',
    'cartButton',
    'cartButtonCount',
  ];
  static #STORAGE_KEY = 'aiagentOverlayState';
  static #PENDING_COLLECTION_FILTERS_KEY = 'aiAgentPendingCollectionFilters';
  static #PERFORMANCE_DIMENSIONS = [
    { key: 'wet_grip', label: 'Grip in the rain', description: "My car feels like it slips a bit when it's wet" },
    { key: 'dry_grip', label: 'Cornering grip', description: 'I want confident handling on a normal dry road' },
    { key: 'braking', label: 'Shorter stopping distance', description: 'I want to stop quickly and safely in an emergency' },
    { key: 'handling', label: 'Responsive steering', description: 'I want the car to feel stable and precise when I turn' },
    { key: 'comfort', label: 'Smooth, comfortable ride', description: "I don't want to feel every bump and pothole" },
    { key: 'noise', label: 'Quiet cabin', description: 'I want less road roar on the highway' },
    { key: 'longevity', label: 'Tyres that last longer', description: "I don't want to replace these again next year" },
    { key: 'energy_efficient', label: 'Better fuel economy', description: 'I want lower rolling resistance to save on fuel' },
    { key: 'value_for_money', label: 'Good value for money', description: "I want a tyre that's worth what I pay" },
    { key: 'evenness_of_wear', label: 'Wears evenly', description: "I don't want it wearing unevenly or needing early replacement" },
  ];
  static #PERFORMANCE_PICKER_REQUIRED_COUNT = 3;
  static #ENQUIRY_RENEWAL_INTERVAL_MS = 60_000;
  static #ENQUIRY_RENEWAL_MINUTES = 5;
  static #ENQUIRY_INACTIVITY_LIMIT_MS = 3 * 60_000;
  static #MAX_REGO_LOOKUPS_WITHOUT_VERIFIED_SESSION = 2;
  #currentEnquiryId = null;
  #enquiryHeartbeatTimer = null;
  #lastCustomerActivityAt = 0;
  #hasGreeted = false;
  #chatSessionHasRecaptchaToken = null;
  #regoLookupCount = 0;
  #customerSendCount = 0;
  #renderedProductCards = [];
  #lastProducts = [];
  #lastRequestedLabels = [];
  #lastRenderView = null;
  #lastFilterSummary = null;
  #lastPageFilters = [];
  #lastAvailabilityFilter = null;
  #lastSearchedSize = null;
  #lastRenderSizeSignature = null;
  #lastRenderIsSplit = false;
  #pendingStoreCandidates = [];
  #lastContextedPath = null;
  #recognition = null;
  #dictationSilenceTimer = null;
  #chatId = null;
  #chatSessionPromise = null;
  #recaptchaChatWidgetId = null;
  #recaptchaTokenResolve = null;
  #recaptchaTokenReject = null;
  get #proxyUrl() {
    return this.dataset.proxyUrl || '';
  }
  connectedCallback() {
    super.connectedCallback();
    this.#restoreState();
    if (window.SpeechRecognition || window.webkitSpeechRecognition) {
      this.refs.chatMic.hidden = false;
    }
    this.addEventListener(DialogCloseEvent.eventName, () => {
      this.#stopEnquiryHeartbeat();
      this.#saveState();
    });
    this.#updateCartBadge();
  }
  openWithMessage(message, hiddenContext = null) {
    this.#clearStaleProductsIfVehicleChanged();
    this.#setNarrowForCurrentProducts();
    this.showDialog();
    this.#updateCartBadge();
    const isEnquiryHandoff = typeof hiddenContext === 'string' && /^ENQUIRY_ID:\s*/.test(hiddenContext);
    this.#greetIfNeeded(isEnquiryHandoff);
    if (message) this.#submitMessage(message, hiddenContext);
    this.#saveState();
  }
  open() {
    this.#clearStaleProductsIfVehicleChanged();
    this.#setNarrowForCurrentProducts();
    this.showDialog();
    this.#updateCartBadge();
    this.#greetIfNeeded();
    this.#saveState();
  }
  /**
   * The chat's product grid is a snapshot tied to whatever vehicle/size was
   * active when it was last rendered. If the customer closes the chat,
   * changes their vehicle or tyre size using the site's own picker, then
   * reopens the chat, that snapshot is no longer about anything on screen -
   * clear it (but keep the conversation history) rather than let the agent
   * keep presenting products for a size that's no longer selected.
   */
  #clearStaleProductsIfVehicleChanged() {
    if (!this.#lastProducts.length) return;
    const currentSignature = this.#getCurrentVehicleSizeSignature();
    if (currentSignature === this.#lastRenderSizeSignature) return;
    this.#clearProducts();
    this.#saveState();
  }
  #setNarrowForCurrentProducts() {
    this.#setNarrow(this.refs.products.hidden);
  }
  close() {
    if (this.#recognition) this.#recognition.stop();
    this.closeDialog();
    this.#saveState();
  }
  expandProducts() {
    this.refs.panes.classList.remove('agent-overlay__panes--chat-expanded');
    this.refs.panes.classList.add('agent-overlay__panes--products-expanded');
  }
  expandChat() {
    this.refs.panes.classList.remove('agent-overlay__panes--products-expanded');
    this.refs.panes.classList.add('agent-overlay__panes--chat-expanded');
  }
  openProductsSheet() {
    if (this.#canShowComparePill() && this.#lastRenderView === 'compare') {
      this.#renderProducts(this.#lastProducts, this.#lastFilterSummary, this.#lastPageFilters, this.#lastRequestedLabels, this.#lastAvailabilityFilter);
    }
    this.#openSheet();
  }
  showCompareView() {
    if (this.#lastRenderView !== 'compare') {
      this.#renderCompare(this.#lastProducts, this.#lastRequestedLabels);
    }
    this.#openSheet();
  }
  #openSheet() {
    this.expandProducts();
    this.refs.panes.classList.add('agent-overlay__panes--sheet-open');
    this.refs.productsPill.classList.remove('agent-overlay__products-pill--unseen');
  }
  closeProductsSheet() {
    this.expandChat();
    this.refs.panes.classList.remove('agent-overlay__panes--sheet-open');
  }
  /**
   * The narrow pill's on:click (mobile action-row and desktop toolbar
   * alike) jumps straight to the same "what's important to you"
   * performance picker the agent can already show, without waiting for
   * the agent to decide to offer it. Switches back to the chat pane
   * (same as closing the mobile sheet) so the picker - it renders into
   * the chat messages, not the product grid - is actually visible right
   * away instead of sitting behind a collapsed/narrow chat pane.
   */
  async narrowResults() {
    this.closeProductsSheet();
    const size = this.#lastSearchedSize;
    if (!size?.width) {
      this.#showPerformancePicker();
      return;
    }
    // Scopes the checkbox list to dimensions a real product in the loaded
    // result set actually qualifies on, same as the agent's own
    // show_performance_picker tool call (see routes/performance.js) -
    // otherwise this could offer a priority (e.g. "Quiet cabin") nothing on
    // screen actually meets. Fails open (shows the full list) on a network
    // error - an unfiltered list beats the pill silently doing nothing.
    const picker = await this.#fetchAvailablePerformanceDimensions(size);
    if (picker?.shown === false) {
      this.#addMessage('agent', "There isn't quite enough variety in what's currently on screen to narrow down by preference - try broadening your search first.");
      return;
    }
    this.#showPerformancePicker(picker?.availableDimensions || null);
  }
  async #fetchAvailablePerformanceDimensions(size) {
    try {
      const response = await fetch(`${this.#proxyUrl}/browser/show-performance-picker`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(size),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }
  #canShowComparePill() {
    const count = this.#lastProducts.length;
    return !this.#lastRenderIsSplit && count >= 2 && count <= 4;
  }
  /**
   * Shared by #updateProductsPill (whether to show the narrow pill at
   * all) and openProductsSheet's own compare-pill counterpart above. A
   * "narrow this down" shortcut only makes sense once there's actually
   * too much to comfortably scan - a small (2-4) or split-fitment result
   * set gets the compare pill instead (see #canShowComparePill).
   */
  #canShowNarrowPill() {
    const count = this.#lastProducts.length;
    return !this.#lastRenderIsSplit && count > 4;
  }
  #updateProductsPill() {
    const count = this.#lastProducts.length;
    this.refs.productsPill.hidden = count === 0;
    this.refs.productsPillLabel.textContent = count ? `${count} tyre${count === 1 ? '' : 's'} found` : 'Tyres found';
    const sheetOpen = this.refs.panes.classList.contains('agent-overlay__panes--sheet-open');
    this.refs.productsPill.classList.toggle('agent-overlay__products-pill--unseen', count > 0 && !sheetOpen);
    const canCompare = this.#canShowComparePill();
    const canNarrow = this.#canShowNarrowPill();
    this.refs.comparePill.hidden = !canCompare;
    this.refs.narrowPill.hidden = !canNarrow;
    if (count > 0) {
      const icon = this.refs.productsPill.querySelector('.agent-overlay__products-pill-icon');
      if (icon) {
        icon.classList.remove('agent-overlay__products-pill-icon--rolling');
        void icon.offsetWidth;
        icon.classList.add('agent-overlay__products-pill-icon--rolling');
      }
    }
  }
  #trackAgentAssistedCart(cartTools) {
    const cartId = this.#getCartId(cartTools);
    if (cartId && typeof cartTools.setCartAttribute === 'function') {
      cartTools.setCartAttribute(cartId, 'agent_assisted', 'true');
    }
    if (typeof window.dataLayer !== 'undefined' && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: 'agentAddToCart' });
    }
  }
  #updateCartBadge() {
    const cartTools = typeof window.cartTools === 'function' ? window.cartTools() : null;
    const cartData = cartTools?.cartData?.value || cartTools?.cartData || null;
    const count = Number(cartData?.totalQuantity) || 0;
    this.refs.cartButton.hidden = count === 0;
    this.refs.cartButtonCount.textContent = count > 99 ? '99+' : String(count);
  }
  openCartDrawer() {
    const cartTools = typeof window.cartTools === 'function' ? window.cartTools() : null;
    if (!cartTools) return;
    cartTools.openCheckout = false;
    cartTools.showCart = true;
  }
  #clearProducts() {
    const { products: productsPane, productsCarousel: productsCarousel, productsFilterSummary: productsFilterSummary } = this.refs;
    productsPane.hidden = true;
    productsCarousel.replaceChildren();
    productsCarousel.hidden = false;
    this.refs.productsLoading.hidden = true;
    productsFilterSummary.replaceChildren();
    productsFilterSummary.hidden = true;
    this.refs.panes.classList.remove('agent-overlay__panes--products-expanded', 'agent-overlay__panes--chat-expanded');
    this.#renderedProductCards = [];
    this.#lastProducts = [];
    this.#lastRenderIsSplit = false;
    this.#updateProductsPill();
    this.#lastRequestedLabels = [];
    this.#lastRenderView = null;
    this.#lastFilterSummary = null;
    this.#lastPageFilters = [];
    this.#lastAvailabilityFilter = null;
    this.#lastSearchedSize = null;
    this.#lastRenderSizeSignature = null;
    this.#setNarrow(true);
  }
  eraseChat() {
    this.refs.messages.replaceChildren();
    this.#clearProducts();
    this.#chatId = null;
    this.#chatSessionPromise = null;
    this.#chatSessionHasRecaptchaToken = null;
    this.#regoLookupCount = 0;
    this.#hasGreeted = false;
    this.#stopEnquiryHeartbeat();
    this.#currentEnquiryId = null;
    this.#lastCustomerActivityAt = 0;
    this.#customerSendCount = 0;
    try {
      sessionStorage.removeItem(AgentOverlayComponent.#STORAGE_KEY);
    } catch (error) {
      console.warn('AI agent chat: failed to clear saved conversation state.', error);
    }
    this.#greetIfNeeded();
  }
  sendMessage(event) {
    event.preventDefault();
    const { chatInput: chatInput } = this.refs;
    const value = chatInput.value.trim();
    if (!value) return;
    chatInput.value = '';
    this.#customerSendCount++;
    this.#submitMessage(value);
    if (this.#customerSendCount === 3) {
      this.#addMessage(
        'agent',
        "Quick note: I'm a beta version of our virtual assistant, so I can occasionally get things wrong - please double-check anything important before relying on it. If you go ahead with a purchase, our store team will also be in touch directly to confirm the details.",
        new Date(),
        'disclaimer',
      );
      this.#saveState();
    }
  }
  toggleDictation() {
    if (this.#recognition) {
      this.#recognition.stop();
      return;
    }
    this.#startDictation();
  }
  #startDictation() {
    const SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) return;
    const { chatInput: chatInput, chatMic: chatMic } = this.refs;
    const recognition = new SpeechRecognitionCtor();
    this.#recognition = recognition;
    if (document.documentElement.lang) recognition.lang = document.documentElement.lang;
    recognition.interimResults = true;
    recognition.continuous = true;
    let autoSendOnEnd = false;
    chatMic.classList.add('agent-overlay__chat-mic--listening');
    chatMic.setAttribute('aria-label', 'Stop and use this');
    chatMic.setAttribute('title', 'Stop and use this');
    chatInput.placeholder = 'Listening…';
    recognition.addEventListener('result', (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      chatInput.value = transcript;
      clearTimeout(this.#dictationSilenceTimer);
      this.#dictationSilenceTimer = setTimeout(() => {
        autoSendOnEnd = true;
        recognition.stop();
      }, 3000);
    });
    recognition.addEventListener('error', (event) => {
      console.error('AI agent dictation failed:', event.error);
    });
    recognition.addEventListener('end', () => {
      clearTimeout(this.#dictationSilenceTimer);
      this.#recognition = null;
      chatMic.classList.remove('agent-overlay__chat-mic--listening');
      chatMic.setAttribute('aria-label', 'Speak your message');
      chatMic.setAttribute('title', 'Speak your message');
      chatInput.placeholder = 'Type your reply…';
      if (autoSendOnEnd && chatInput.value.trim()) {
        this.sendMessage({ preventDefault: () => {} });
      }
      chatInput.focus();
    });
    recognition.start();
  }
  #greetIfNeeded(isEnquiryHandoff = false) {
    if (this.#hasGreeted) return;
    this.#hasGreeted = true;
    if (!this.#proxyUrl) {
      this.#addMessage('agent', 'Sorry, chat is unavailable at the moment.');
      this.#saveState();
      return;
    }
    this.#addMessage(
      'agent',
      isEnquiryHandoff
        ? 'We are connecting you to our virtual assistant.'
        : "Hi! I can help you find the right tyres. What's your rego number, or your tyre size (width / aspect ratio / rim diameter)?",
    );
    this.#saveState();
    this.#ensureChatSession();
  }
  #submitMessage(value, hiddenContext = null) {
    this.#addMessage('user', value);
    this.#lastCustomerActivityAt = Date.now();
    this.#saveState();
    const isEnquiryHandoff = typeof hiddenContext === 'string' && /^ENQUIRY_ID:\s*/.test(hiddenContext);
    const enquiryIdMatch = isEnquiryHandoff ? /^ENQUIRY_ID:\s*(.+)$/.exec(hiddenContext) : null;
    if (isEnquiryHandoff) {
      if (enquiryIdMatch) {
        this.#currentEnquiryId = enquiryIdMatch[1].trim();
        this.#startEnquiryHeartbeat();
      }
      this.#addMessage(
        'agent',
        "Hi, your enquiry was submitted to the store - but please give me a chance to try to solve your query right here, right now. I'm a well-trained agent, though still a beta version!",
      );
      this.#saveState();
    }
    if (this.#proxyUrl) {
      // Labelled explicitly so the actual customer utterance stays
      // unambiguous even when PAGE_CONTEXT (prepended in #realAgentReply) is
      // large - a bare value like "BGT68S" sitting right after a long
      // PAGE_CONTEXT block with no label of its own is easy to mistake for
      // part of that context rather than "the thing to actually respond
      // to", versus internal context markers elsewhere in this file, which
      // are already clearly self-describing (e.g.
      // [[VEHICLE_LOOKUP_RESULT: ...]]). Skipped for
      // the enquiry-handoff value - it already starts with "The customer
      // submitted an enquiry..." and doesn't need a second, redundant label.
      const outgoingValue = isEnquiryHandoff ? value : `Customer said: ${value}`;
      this.#realAgentReply(outgoingValue, hiddenContext, { suppressTextReply: isEnquiryHandoff });
    } else {
      this.#addMessage('agent', 'Sorry, chat is unavailable at the moment.');
      this.#saveState();
    }
  }
  #startEnquiryHeartbeat() {
    if (this.#enquiryHeartbeatTimer || !this.#currentEnquiryId || !this.#proxyUrl) return;
    this.#enquiryHeartbeatTimer = setInterval(() => {
      this.#renewPendingEnquiry();
    }, AgentOverlayComponent.#ENQUIRY_RENEWAL_INTERVAL_MS);
  }
  #stopEnquiryHeartbeat() {
    if (!this.#enquiryHeartbeatTimer) return;
    clearInterval(this.#enquiryHeartbeatTimer);
    this.#enquiryHeartbeatTimer = null;
  }
  async #renewPendingEnquiry() {
    if (!this.#currentEnquiryId || !this.#proxyUrl) return;
    if (Date.now() - this.#lastCustomerActivityAt >= AgentOverlayComponent.#ENQUIRY_INACTIVITY_LIMIT_MS) {
      this.#stopEnquiryHeartbeat();
      await this.#submitPendingEnquiryNow();
      return;
    }
    try {
      await fetch(`${this.#proxyUrl}/browser/renew-enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enquiryId: this.#currentEnquiryId }),
      });
    } catch (error) {
      console.warn('AI agent chat: failed to renew pending enquiry.', error);
    }
  }
  async #submitPendingEnquiryNow() {
    const enquiryId = this.#currentEnquiryId;
    this.#currentEnquiryId = null;
    this.#saveState();
    if (!enquiryId || !this.#proxyUrl) return;
    try {
      await fetch(`${this.#proxyUrl}/browser/submit-enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enquiryId }),
      });
    } catch (error) {
      console.warn('AI agent chat: failed to submit pending enquiry after inactivity.', error);
    }
  }
  #addMessage(role, text, timestamp = new Date(), variant = null) {
    const { messages: messages } = this.refs;
    let displayText = text;
    let options = [];
    if (role === 'agent') {
      options = [...text.matchAll(/\[\[([^\]]+)\]\]/g)].map((match) => match[1].trim());
      if (options.length) {
        displayText = text
          .replace(/\[\[([^\]]+)\]\]/g, '$1')
          .replace(/\s{2,}/g, ' ')
          .trim();
      }
    }
    const bubble = document.createElement('div');
    bubble.className = `agent-overlay__message agent-overlay__message--${role}`;
    if (variant) bubble.classList.add(`agent-overlay__message--${variant}`);
    bubble.dataset.timestamp = timestamp.toISOString();
    const textEl = document.createElement('span');
    textEl.className = 'agent-overlay__message-text';
    this.#renderFormattedText(textEl, this.#normalizeListFormatting(displayText));
    bubble.appendChild(textEl);
    const timeEl = document.createElement('span');
    timeEl.className = 'agent-overlay__message-time';
    timeEl.textContent = this.#formatTimestamp(timestamp);
    bubble.appendChild(timeEl);
    messages.appendChild(bubble);
    if (options.length) this.#addQuickReplies(options);
    messages.scrollTop = messages.scrollHeight;
    return bubble;
  }
  #normalizeListFormatting(text) {
    const occurrences = text.match(/ - /g);
    if (!occurrences || occurrences.length < 2) return text;
    return text.replace(/ - /g, '\n- ');
  }
  #renderFormattedText(container, text) {
    const segments = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|__[^_]+__)/g);
    for (const segment of segments) {
      const boldMatch = segment.match(/^\*\*([^*]+)\*\*$/);
      const italicMatch = !boldMatch && segment.match(/^\*([^*]+)\*$/);
      const questionMatch = !boldMatch && !italicMatch && segment.match(/^__([^_]+)__$/);
      if (boldMatch) {
        const strong = document.createElement('strong');
        strong.textContent = boldMatch[1];
        container.appendChild(strong);
      } else if (italicMatch) {
        const em = document.createElement('em');
        em.textContent = italicMatch[1];
        container.appendChild(em);
      } else if (questionMatch) {
        const mark = document.createElement('mark');
        mark.textContent = questionMatch[1];
        container.appendChild(mark);
      } else if (segment) {
        container.appendChild(document.createTextNode(segment));
      }
    }
  }
  #addQuickReplies(options) {
    const { messages: messages } = this.refs;
    const container = document.createElement('div');
    container.className = 'agent-overlay__quick-replies';
    const sortedOptions = [...options].sort((a, b) =>
      a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    );
    for (const option of sortedOptions) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'agent-overlay__quick-reply';
      const matchedProduct = this.#findKnownProductByTitle(option);
      let label = option;
      if (matchedProduct) {
        const price = this.#formatPrice(matchedProduct.price);
        if (price) label += ` — ${price}`;
        const promo = this.#describePromo(matchedProduct);
        if (promo) label += ` 🏷️ ${promo}`;
      }
      button.textContent = label;
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        container.querySelectorAll('.agent-overlay__quick-reply--selected').forEach((el) => el.classList.remove('agent-overlay__quick-reply--selected'));
        button.classList.add('agent-overlay__quick-reply--selected');
        this.#submitMessage(matchedProduct ? `Tell me more about ${option}` : option);
        this.#loadProductForQuickReply(option, matchedProduct);
      });
      container.appendChild(button);
    }
    messages.appendChild(container);
  }
  #addTipBubble(tip) {
    const { messages: messages } = this.refs;
    const bubble = document.createElement('div');
    bubble.className = 'agent-overlay__message agent-overlay__message--tip';
    const icon = document.createElement('span');
    icon.className = 'agent-overlay__tip-icon';
    icon.textContent = '💡';
    icon.setAttribute('aria-hidden', 'true');
    bubble.appendChild(icon);
    const body = document.createElement('span');
    body.className = 'agent-overlay__tip-body';
    const textEl = document.createElement('span');
    textEl.className = 'agent-overlay__message-text';
    textEl.textContent = tip.text;
    body.appendChild(textEl);
    if (tip.example) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'agent-overlay__tip-example';
      button.textContent = tip.example;
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        if (tip.action === 'show_performance_picker') {
          button.disabled = true;
          this.#showPerformancePicker(tip.availableDimensions);
          return;
        }
        this.#submitMessage(tip.example);
      });
      body.appendChild(button);
    }
    bubble.appendChild(body);
    messages.appendChild(bubble);
    messages.scrollTop = messages.scrollHeight;
    return bubble;
  }
  #buildPageContext() {
    const path = window.location.pathname;
    const parts = [`page "${document.title || path}" (${path})`];
    const product = window.afterpay_product;
    if (product && typeof product === 'object' && product.title) {
      const price = typeof product.price === 'number' ? this.#formatPrice(product.price / 100) : null;
      parts.push(`viewing product "${product.title}"${product.vendor ? ` by ${product.vendor}` : ''}${price ? `, ${price}` : ''}${product.type ? ` (${product.type})` : ''}`);
    }
    try {
      const collectionStore = typeof window.collectionStore === 'function' ? window.collectionStore() : null;
      const count = collectionStore?.products?.edges?.length;
      if (typeof count === 'number' && path.includes('/collections/')) {
        parts.push(`browsing a collection page currently showing ${count} product(s) (exact applied filters not visible to you - ask the customer directly if it matters)`);
      }
    } catch (error) {
      console.warn('AI agent chat: skipped page-awareness context.', error);
    }
    try {
      const vehicleSearch = typeof window.vehicleSearch === 'function' ? window.vehicleSearch() : null;
      const activeVehicleId = vehicleSearch?.activeVehicleId;
      const activeVehicle =
        activeVehicleId && Array.isArray(vehicleSearch?.vehicles)
          ? vehicleSearch.vehicles.find((v) => String(v?.id) === String(activeVehicleId) || String(v?.vehicle_id) === String(activeVehicleId))
          : null;
      const tyreFitments = activeVehicle?.fitmentInfo?.tyre_fitments;
      if (activeVehicle && Array.isArray(tyreFitments) && tyreFitments.length) {
        const makeModel = [activeVehicle.make?.display_name, activeVehicle.model?.display_name, activeVehicle.display_name].filter(Boolean).join(' ');
        const current = tyreFitments[0];
        const currentFront = current?.f_tyres?.full_desc || current?.f_tyres?.desc;
        const currentDesc = currentFront ? (current.is_split ? `front ${currentFront}, rear ${current?.r_tyres?.full_desc || current?.r_tyres?.desc}` : currentFront) : null;
        const alternateCount = tyreFitments.length - 1;
        const evNote = activeVehicle.is_electric ? ` — THIS IS AN ELECTRIC VEHICLE: do not use driving style "everyday" or default any driving style for it. Do NOT hard-filter by labels: ["electric_vehicle"] either - most tyres in the right size are perfectly legal and fit for an EV, so filtering to only EV-labelled stock hides genuinely valid options from the customer. Instead, search normally (full results, no label filter), and if an EV-labelled tyre is among them, use recommendedHandle to highlight it and briefly explain why it may suit an EV better (EVs are significantly heavier and deliver more instant torque than an equivalent petrol vehicle, so an EV-rated tyre wears more evenly and grips more predictably under that load) - a recommendation, not an exclusion` : activeVehicle.is_hybrid ? " — this is a hybrid vehicle: it is heavier than the equivalent petrol version, so weigh that when picking tyres, but don't hard-filter on it either" : '';
        const vehicleIdForContext = activeVehicle.id ?? activeVehicle.vehicle_id ?? null;
        const currentFitmentId = current?.id ?? null;
        const alternateFitmentIds = tyreFitments
          .slice(1)
          .map((f) => f?.id)
          .filter(Boolean);
        const idsNote =
          vehicleIdForContext && currentFitmentId
            ? ` [vehicleId=${vehicleIdForContext}, fitmentId=${currentFitmentId}${alternateFitmentIds.length ? `, alternate fitmentIds=${alternateFitmentIds.join(',')}` : ''}]`
            : '';
        const frontAxleMatches = Array.isArray(current?.f_tyres?.matches) ? current.f_tyres.matches : [];
        const rearAxleMatches = current?.is_split && Array.isArray(current?.r_tyres?.matches) ? current.r_tyres.matches : [];
        const axleMatchesNote = frontAxleMatches.length
          ? ` [REQUIRED every time you call search_products for this vehicle: axleMatches=${JSON.stringify(frontAxleMatches)}${
              rearAxleMatches.length ? `, rearAxleMatches=${JSON.stringify(rearAxleMatches)}` : ''
            } - these are this vehicle's own accepted size+load+speed specs; pass them as a hard filter alongside width/aspectRatio/rimDiameter, not instead of them, on every search, not just the first one]`
          : '';
        const wheelAlignmentHandle = activeVehicle?.wheelAlignment?.handle || null;
        const wheelAlignmentNote = wheelAlignmentHandle
          ? ` [this vehicle has its own Wheel Alignment product - pass wheelAlignmentHandle="${wheelAlignmentHandle}" on every search_products call for it (mode "present", not compare), so it can be mixed into the results, same as every other search for this vehicle]`
          : '';
        parts.push(`has a vehicle already selected in the site's own vehicle picker${makeModel ? `: ${makeModel}` : ''}` + `${activeVehicle.rego ? ` (rego ${activeVehicle.rego}${activeVehicle.regoState ? ` ${activeVehicle.regoState}` : ''})` : ''}` + `${currentDesc ? `, currently set to ${currentDesc}` : ''}` + `${alternateCount > 0 ? ` (${alternateCount} alternate fitment(s) also available - if the customer wants to see or switch to one, tell them to use the size dropdown right next to the vehicle selector at the top of the page, which already lists every one of them with its real size and switches instantly when clicked - never list, describe, or guess the alternate sizes yourself)` : ''}` + idsNote + axleMatchesNote + wheelAlignmentNote + evNote);
      } else {
        const selectedSizeDisplay = this.#safeLocalStorageGet('selectedSizeDisplay');
        if (selectedSizeDisplay) {
          parts.push(`has selected a tyre size directly (no vehicle picked): ${selectedSizeDisplay}`);
        }
      }
    } catch (error) {
      console.warn('AI agent chat: skipped vehicle context.', error);
    }
    try {
      const storeSelector = typeof window.storeSelector === 'function' ? window.storeSelector() : null;
      let nearestStore = storeSelector?.nearestStore || null;
      if (!nearestStore) {
        const raw = this.#safeLocalStorageGet('currentNearestStore');
        try {
          nearestStore = raw ? JSON.parse(raw) : null;
        } catch {
          nearestStore = null;
        }
      }
      if (nearestStore?.node?.name) {
        const provinceCode = nearestStore.node.address?.provinceCode;
        parts.push(`already has a store selected for stock/location purposes: ${nearestStore.node.name}${provinceCode ? ` (${provinceCode})` : ''} - do NOT ask for postcode/suburb again UNLESS the customer explicitly wants to change their location or their selected store; in that case call find_nearest_store with the new postcode/suburb, then - once the customer confirms which of its returned stores they actually want - call select_store with that store's id (never assume the nearest one is the one they meant)`);
      }
    } catch (error) {
      console.warn('AI agent chat: skipped store context.', error);
    }
    return parts.join('; ');
  }
  #hasActiveVehicleSelected() {
    try {
      const vehicleSearch = typeof window.vehicleSearch === 'function' ? window.vehicleSearch() : null;
      return !!vehicleSearch?.activeVehicleId;
    } catch (error) {
      console.warn('AI agent chat: skipped active-vehicle check.', error);
      // Err toward NOT resetting the vehicle/size on an unexpected failure
      // here - see the call site's comment on why this must never be a
      // casual side effect.
      return true;
    }
  }
  /**
   * A short string that changes whenever the site-wide vehicle, its current
   * fitment, or the directly-selected tyre size changes - used to detect
   * that the chat's own cached product grid (rendered for a previous
   * vehicle/size) is now stale. Mirrors the vehicle-lookup logic in
   * #buildPageContext/#getActiveVehicleFitmentSizes so all three agree on
   * what "the active vehicle" means. On error, returns the last-known
   * signature so a transient failure never looks like a change.
   */
  #getCurrentVehicleSizeSignature() {
    try {
      const vehicleSearch = typeof window.vehicleSearch === 'function' ? window.vehicleSearch() : null;
      const activeVehicleId = vehicleSearch?.activeVehicleId;
      const activeVehicle =
        activeVehicleId && Array.isArray(vehicleSearch?.vehicles)
          ? vehicleSearch.vehicles.find((v) => String(v?.id) === String(activeVehicleId) || String(v?.vehicle_id) === String(activeVehicleId))
          : null;
      const tyreFitments = activeVehicle?.fitmentInfo?.tyre_fitments;
      if (activeVehicle && Array.isArray(tyreFitments) && tyreFitments.length) {
        const currentFitmentId = tyreFitments[0]?.id ?? '0';
        return `vehicle:${activeVehicleId}:${currentFitmentId}`;
      }
      const selectedSizeDisplay = this.#safeLocalStorageGet('selectedSizeDisplay');
      return selectedSizeDisplay ? `size:${selectedSizeDisplay}` : '';
    } catch (error) {
      console.warn('AI agent chat: skipped vehicle size signature check.', error);
      return this.#lastRenderSizeSignature || '';
    }
  }
  /**
   * `isFirstReply` (the very first reply of a brand-new chat session,
   * before #chatId exists) uses a slightly longer label than the usual
   * "Mike is typing" - that first reply is genuinely slower (reCAPTCHA +
   * creating the chat session, THEN the actual completion call).
   */
  #showTyping(isFirstReply = false) {
    const { messages: messages } = this.refs;
    const bubble = document.createElement('div');
    bubble.className = 'agent-overlay__message agent-overlay__message--agent agent-overlay__message--typing';
    const dots = document.createElement('span');
    dots.className = 'agent-overlay__typing-dots';
    dots.setAttribute('role', 'status');
    const label = document.createElement('span');
    label.className = 'agent-overlay__typing-label';
    dots.appendChild(label);
    for (let i = 0; i < 3; i++) {
      dots.appendChild(document.createElement('span')).className = 'agent-overlay__typing-dot';
    }
    bubble.appendChild(dots);
    label.textContent = isFirstReply ? 'Mike, our virtual assistant, is typing' : 'Mike is typing';
    messages.appendChild(bubble);
    messages.scrollTop = messages.scrollHeight;
    return bubble;
  }
  #removeTyping(bubble) {
    if (!bubble) return;
    if (bubble._countdownInterval) clearInterval(bubble._countdownInterval);
    bubble.remove();
  }
  async #getRecaptchaToken() {
    if (typeof window.grecaptcha === 'undefined') return null;
    const siteKey = document.getElementById('recaptcha')?.getAttribute('data-sitekey');
    if (!siteKey) return null;
    try {
      if (this.#recaptchaChatWidgetId == null) {
        const container = document.createElement('div');
        container.style.display = 'none';
        document.body.appendChild(container);
        this.#recaptchaChatWidgetId = window.grecaptcha.render(container, {
          sitekey: siteKey,
          size: 'invisible',
          callback: (token) => this.#recaptchaTokenResolve?.(token),
          'error-callback': () => this.#recaptchaTokenReject?.(new Error('reCAPTCHA error')),
        });
      }
      return await new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => reject(new Error('reCAPTCHA timed out')), 15000);
        this.#recaptchaTokenResolve = (token) => {
          clearTimeout(timeoutId);
          resolve(token);
        };
        this.#recaptchaTokenReject = (error) => {
          clearTimeout(timeoutId);
          reject(error);
        };
        window.grecaptcha.execute(this.#recaptchaChatWidgetId);
      });
    } catch (error) {
      console.warn('AI agent chat: reCAPTCHA token unavailable, continuing without one.', error);
      return null;
    }
  }
  #ensureChatSession() {
    if (this.#chatId) return Promise.resolve(this.#chatId);
    if (!this.#chatSessionPromise) {
      this.#chatSessionPromise = this.#getRecaptchaToken()
        .catch(() => null)
        .then((recaptchaToken) => {
          this.#chatSessionHasRecaptchaToken = !!recaptchaToken;
          return fetch(`${this.#proxyUrl}/chat`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ recaptchaToken }),
          });
        })
        .then((response) => response.json())
        .then((data) => {
          if (!data.chat_id) throw new Error('No chat_id in response');
          this.#chatId = data.chat_id;
          this.#saveState();
          return this.#chatId;
        })
        .catch((error) => {
          this.#chatSessionPromise = null;
          throw error;
        });
    }
    return this.#chatSessionPromise;
  }
  async #realAgentReply(value, hiddenContext = null, { suppressTextReply = false, silent = false } = {}) {
    const typingEl = silent ? null : this.#showTyping(!this.#chatId);
    try {
      const chatId = await this.#ensureChatSession();
      const path = window.location.pathname;
      let outgoingContent = value;
      if (this.#lastContextedPath !== path) {
        this.#lastContextedPath = path;
        outgoingContent = `[[PAGE_CONTEXT: ${this.#buildPageContext()}]]\n${outgoingContent}`;
      }
      if (hiddenContext) {
        outgoingContent = `[[${hiddenContext}]]\n${outgoingContent}`;
      }
      const response = await fetch(`${this.#proxyUrl}/chat-completion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chat_id: chatId,
          content: outgoingContent,
        }),
      });
      const data = await response.json();
      this.#removeTyping(typingEl);
      if (!response.ok) {
        console.error('AI agent proxy error:', data);
        this.#addMessage('agent', "Sorry, I'm having trouble connecting right now — please try again in a moment.");
        return;
      }
      const allMessages = data.messages || [];
      const agentMessages = allMessages.filter((message) => message.role === 'agent' && typeof message.content === 'string');
      if (suppressTextReply) {
      } else if (!agentMessages.length) {
        this.#addMessage('agent', "Sorry, I didn't get a reply — could you try that again?");
        return;
      } else {
        for (const message of agentMessages) {
          this.#addMessage('agent', message.content);
        }
      }
      const toolResults = allMessages.filter((message) => message.role === 'tool_call_result');
      // tool_call_invocation.arguments survives intact even when the
      // matching tool_call_result's own content gets reduced to almost
      // nothing (see #fetchDirectSearchResult's
      // doc comment for why) - so a search_products invocation's args are a
      // reliable source to re-run the exact same search directly, bypassing
      // whatever the result's own content does or doesn't carry.
      const invocationsByCallId = new Map();
      for (const message of allMessages) {
        if (message.role !== 'tool_call_invocation' || !message.tool_call_id) continue;
        let args = message.arguments;
        if (typeof args === 'string') {
          try {
            args = JSON.parse(args);
          } catch {
            args = null;
          }
        }
        if (args && typeof args === 'object') {
          invocationsByCallId.set(message.tool_call_id, { name: message.name, args });
        }
      }
      if (silent && toolResults.length) {
        // A "silent" call is meant to be pure background bookkeeping (see
        // the stock-check continuation in #refreshStockForVisibleProducts)
        // - the agent isn't supposed to take any new visible action from
        // it. If it calls a tool anyway, that tool's own render (e.g. an
        // empty/different search_products result) must NOT be allowed to
        // reach the screen - bail out here instead of letting it flow into
        // productRenderQueue below, or it silently overwrites whatever is
        // already correctly on screen with nothing in the UI ever
        // indicating it happened.
        console.warn('AI agent chat: a silent background continuation triggered tool call(s) - ignoring their output.', toolResults);
        return;
      }
      const productRenderQueue = [];
      const addToCartQueue = [];
      let didSomethingVisible = false;
      for (const result of toolResults) {
        const invocation = result.tool_call_id ? invocationsByCallId.get(result.tool_call_id) : null;
        const parsedProducts =
          invocation?.name === 'search_products' && invocation.args?.mode !== 'info'
            ? await this.#fetchDirectSearchResult(invocation.args, result.content)
            : this.#parseToolResult(result.content);
        if (parsedProducts && parsedProducts.mode !== 'info') {
          // Only sync the header's plain-size widget (and drop any active
          // vehicle) when there's genuinely no vehicle selected yet - e.g.
          // the customer typed a raw size directly. `fitsVehicle` is just
          // whether THIS result set happens to include an OE-marked match
          // (rare for a generic brand/price search), not whether a vehicle
          // is still selected - using it here was a real bug: searching
          // "other brands" or "cheapest 6" for an already-selected vehicle
          // (which legitimately returns zero OE matches most of the time)
          // was silently wiping the customer's vehicle and split fitment
          // from the site's own header. Never reset the vehicle as a
          // side effect of what a search happened to return.
          if (!this.#hasActiveVehicleSelected()) this.#syncFitmentWithSearchedSize(parsedProducts.searchedSize);
          productRenderQueue.push(parsedProducts);
          continue;
        }
        const storeResult = this.#parseStoreResult(result.content);
        if (storeResult && storeResult.stores.length) {
          this.#pendingStoreCandidates = storeResult.stores;
          if (storeResult.customerLocation) {
            this.#setCustomerLocation(storeResult.customerLocation);
          }
          const storeSelector = typeof window.storeSelector === 'function' ? window.storeSelector() : null;
          const hasExistingStore = !!storeSelector?.nearestStore || !!this.#safeLocalStorageGet('currentNearestStore');
          if (!hasExistingStore) {
            this.#selectStore(storeResult.stores[0]);
          }
          this.#saveState();
          await this.#refreshStockForVisibleProducts(null, storeResult.stores);
          didSomethingVisible = true;
          continue;
        }
        const selectStoreResult = this.#parseSelectStoreResult(result.content);
        if (selectStoreResult) {
          const chosen = this.#pendingStoreCandidates.find((s) => this.#stripGid(s.id) === this.#stripGid(selectStoreResult.storeId));
          if (chosen) {
            this.#selectStore(chosen);
            this.#saveState();
            await this.#refreshStockForVisibleProducts();
            didSomethingVisible = true;
          } else {
            await this.#continueConversationWithContext(
              '[[STORE_SELECT_FAILED: that store id is not one of the candidates find_nearest_store just returned - call find_nearest_store again first, then call select_store with one of ITS ids]]'
            );
            didSomethingVisible = true;
          }
          continue;
        }
        const vehicleResult = this.#parseVehicleResult(result.content);
        if (vehicleResult) {
          this.#applyVehicleResult(vehicleResult);
          didSomethingVisible = true;
          continue;
        }
        const addToCartResult = this.#parseAddToCartResult(result.content);
        if (addToCartResult) {
          addToCartQueue.push(addToCartResult);
          continue;
        }
        const tipResult = this.#parseTipResult(result.content);
        if (tipResult) {
          this.#addTipBubble(tipResult);
          didSomethingVisible = true;
          continue;
        }
        const performancePickerResult = this.#parseShowPerformancePickerResult(result.content);
        if (performancePickerResult) {
          this.#showPerformancePicker(performancePickerResult.availableDimensions);
          didSomethingVisible = true;
        }
      }
      if (productRenderQueue.length) await this.#renderProductResults(productRenderQueue);
      for (const addToCartResult of addToCartQueue) {
        await this.#addToCartFromAgent(addToCartResult.handle, addToCartResult.quantity);
      }
      const anyProductsShown = productRenderQueue.some((entry) => Array.isArray(entry?.products) && entry.products.length > 0);
      if (suppressTextReply && !silent && agentMessages.length && !didSomethingVisible && !anyProductsShown && !addToCartQueue.length) {
        for (const message of agentMessages) {
          this.#addMessage('agent', message.content);
        }
      }
    } catch (error) {
      this.#removeTyping(typingEl);
      console.error('AI agent proxy request failed:', error);
      this.#addMessage('agent', "Sorry, I'm having trouble connecting right now — please try again in a moment.");
    } finally {
      this.#saveState();
    }
  }
  #emptyProductResult() {
    return { mode: 'present', products: [], filterSummary: null, pageFilters: [], requestedLabels: [], availabilityFilter: null, view: 'grid', searchedSize: null };
  }
  #parseToolResult(content) {
    const looksLikeProductsPayload = typeof content === 'string' && content.includes('"products"');
    let parsed = content;
    if (typeof content === 'string') {
      try {
        parsed = JSON.parse(content);
      } catch (error) {
        // A search_products tool result that fails to parse here renders
        // as nothing at all (the customer sees no products and no error) -
        // logging the raw length/tail is the only way to tell a genuinely
        // truncated payload (e.g. cut off mid-JSON upstream) apart from a
        // one-off fluke, since this content isn't visible anywhere else.
        if (looksLikeProductsPayload) {
          console.error('AI agent chat: search_products result looked like a products payload but failed to parse.', { length: content.length, tail: content.slice(-200), error });
        }
        return looksLikeProductsPayload ? this.#emptyProductResult() : null;
      }
    }
    const products = parsed?.products;
    if (!Array.isArray(products)) {
      if (looksLikeProductsPayload) {
        console.error('AI agent chat: search_products result parsed but had no products array.', parsed);
      }
      return looksLikeProductsPayload ? this.#emptyProductResult() : null;
    }
    const mode = parsed?.mode === 'info' ? 'info' : 'present';
    const filterSummary = parsed?.filterSummary;
    const pageFiltersRaw = parsed?.pageFilters;
    const pageFilters = Array.isArray(pageFiltersRaw) ? pageFiltersRaw.filter((f) => typeof f === 'string') : [];
    const requestedLabelsRaw = parsed?.requestedLabels;
    const requestedLabels = Array.isArray(requestedLabelsRaw) ? requestedLabelsRaw.filter((l) => typeof l === 'string') : [];
    const availabilityFilterRaw = parsed?.availabilityFilter;
    const availabilityFilter = typeof availabilityFilterRaw === 'number' && Number.isFinite(availabilityFilterRaw) ? availabilityFilterRaw : null;
    const view = parsed?.view === 'compare' ? 'compare' : 'grid';
    const searchedSizeRaw = parsed?.searchedSize;
    const searchedSize =
      searchedSizeRaw && searchedSizeRaw.width && searchedSizeRaw.aspectRatio && searchedSizeRaw.rimDiameter
        ? {
            width: String(searchedSizeRaw.width),
            aspectRatio: String(searchedSizeRaw.aspectRatio),
            rimDiameter: String(searchedSizeRaw.rimDiameter),
          }
        : null;
    return {
      mode: mode,
      products: products,
      filterSummary: typeof filterSummary === 'string' ? filterSummary : null,
      pageFilters: pageFilters,
      requestedLabels: requestedLabels,
      availabilityFilter: availabilityFilter,
      view: view,
      searchedSize: searchedSize,
    };
  }
  #parseStoreResult(content) {
    let parsed = content;
    if (typeof content === 'string') {
      try {
        parsed = JSON.parse(content);
      } catch {
        return null;
      }
    }
    const stores = parsed?.stores;
    if (!Array.isArray(stores)) return null;
    const customerLocationRaw = parsed?.customerLocation;
    const lat = Number(customerLocationRaw?.latitude);
    const lng = Number(customerLocationRaw?.longitude);
    const customerLocation =
      Number.isFinite(lat) && Number.isFinite(lng)
        ? {
            latitude: lat,
            longitude: lng,
            label: typeof customerLocationRaw?.label === 'string' ? customerLocationRaw.label : null,
          }
        : null;
    return {
      stores: stores,
      customerLocation: customerLocation,
    };
  }
  #parseSelectStoreResult(content) {
    let parsed = content;
    if (typeof content === 'string') {
      try {
        parsed = JSON.parse(content);
      } catch {
        return null;
      }
    }
    if (parsed?.selected !== true) return null;
    const storeId = parsed?.storeId;
    if (typeof storeId !== 'string' && typeof storeId !== 'number') return null;
    return { storeId: String(storeId) };
  }
  #parseVehicleResult(content) {
    let parsed = content;
    if (typeof content === 'string') {
      try {
        parsed = JSON.parse(content);
      } catch {
        return null;
      }
    }
    const result = parsed;
    const hasValidFound = typeof result?.found === 'boolean' || result?.found === null;
    if (!result || !hasValidFound || (!result.needsClientLookup && !Array.isArray(result.fitments))) return null;
    return result;
  }
  #parseAddToCartResult(content) {
    let parsed = content;
    if (typeof content === 'string') {
      try {
        parsed = JSON.parse(content);
      } catch {
        return null;
      }
    }
    const result = parsed;
    if (result?.addedToCart !== true || typeof result?.handle !== 'string') return null;
    const quantity = Number(result.quantity);
    return {
      handle: result.handle,
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 4,
    };
  }
  #parseTipResult(content) {
    let parsed = content;
    if (typeof content === 'string') {
      try {
        parsed = JSON.parse(content);
      } catch {
        return null;
      }
    }
    const result = parsed;
    if (result?.shownTip !== true || typeof result?.tipText !== 'string' || !result.tipText.trim()) return null;
    const example = typeof result.exampleQuery === 'string' && result.exampleQuery.trim() ? result.exampleQuery.trim() : null;
    const action = result.action === 'show_performance_picker' ? result.action : null;
    const availableDimensions = Array.isArray(result.availableDimensions) ? result.availableDimensions : null;
    return { text: result.tipText.trim(), example, action, availableDimensions };
  }
  #parseShowPerformancePickerResult(content) {
    let parsed = content;
    if (typeof content === 'string') {
      try {
        parsed = JSON.parse(content);
      } catch {
        return null;
      }
    }
    if (parsed?.shown !== true) return null;
    const availableDimensions = Array.isArray(parsed?.availableDimensions) ? parsed.availableDimensions : null;
    return { availableDimensions };
  }
  async #addToCartFromAgent(handle, quantity) {
    const product = this.#lastProducts.find((p) => p?.handle === handle);
    if (!product) {
      this.#addMessage('agent', "Sorry, I couldn't find that exact product to add — please try from the product page.");
      return;
    }
    if (product.isUpsellService) quantity = 1;
    const cartTools = typeof window.cartTools === 'function' ? window.cartTools() : null;
    if (!cartTools) {
      this.#addMessage('agent', "Sorry, I can't add to cart from here right now — please add it from the product page instead.");
      return;
    }
    const storeSelector = typeof window.storeSelector === 'function' ? window.storeSelector() : null;
    let currentStore = storeSelector?.nearestStore || null;
    if (!currentStore) {
      const raw = this.#safeLocalStorageGet('currentNearestStore');
      try {
        currentStore = raw ? JSON.parse(raw) : null;
      } catch {
        currentStore = null;
      }
    }
    if (!currentStore) {
      this.#addMessage('agent', 'I just need your postcode or suburb first, so I can check stock at your nearest store before adding this to your cart.');
      return;
    }
    const cardEntry = this.#renderedProductCards.find((c) => c?.product?.handle === handle);
    const foundAt = cardEntry?.knownFoundAt;
    const currentStoreId = this.#stripGid(currentStore?.node?.id);
    if (foundAt?.rawNode && cardEntry.knownInStock && foundAt.id !== currentStoreId) {
      this.#selectStore({
        id: foundAt.rawNode.id,
        name: foundAt.rawNode.name,
        address: foundAt.rawNode.address?.formatted,
        phone: foundAt.rawNode.address?.phone,
        latitude: foundAt.rawNode.address?.latitude,
        longitude: foundAt.rawNode.address?.longitude,
        provinceCode: foundAt.rawNode.address?.provinceCode,
      });
      this.#saveState();
    }
    const variantId = this.#stripGid(product?.variantId);
    if (!variantId) {
      this.#addMessage('agent', "Sorry, I couldn't find that exact product to add — please try from the product page.");
      return;
    }
    try {
      const vehicleSearch = typeof window.vehicleSearch === 'function' ? window.vehicleSearch() : null;
      const cartId = this.#getCartId(cartTools);
      const promo = this.#isFitmentFeeEligible(product);
      if (cartId) {
        await cartTools.cartAdd(variantId, quantity, cartId, promo);
      } else {
        await cartTools.newCart(variantId, quantity, vehicleSearch?.vehicles || [], storeSelector?.location, promo);
      }
      cartTools.openCheckout = false;
      cartTools.showCart = true;
      this.#updateCartBadge();
      this.#trackAgentAssistedCart(cartTools);
      this.close();
    } catch (error) {
      console.error('AI agent add-to-cart (agent-triggered) failed:', error);
      this.#addMessage('agent', 'Sorry, something went wrong adding that to your cart — please try again.');
    }
  }
  #selectStore(store) {
    if (typeof window.storeSelector !== 'function') {
      console.warn('storeSelector Pinia store not available - store selection was not applied site-wide.');
      return;
    }
    const node = {
      id: store.id,
      name: store.name,
      address: {
        formatted: store.address,
        phone: store.phone,
        latitude: store.latitude,
        longitude: store.longitude,
        provinceCode: store.provinceCode,
      },
    };
    window.storeSelector().storeSelect({
      node: node,
    });
  }
  #setCustomerLocation(location) {
    const storeSelector = typeof window.storeSelector === 'function' ? window.storeSelector() : null;
    if (!storeSelector || typeof storeSelector.locationSelect !== 'function') return;
    storeSelector.locationSelect(location.label || '', location.latitude, location.longitude);
  }
  #buildVehicleFromFindResult(result) {
    const tyreFitments = (Array.isArray(result.fitments) ? result.fitments : []).map((f) => ({
      id: f.id,
      is_oe: !!f.isOe,
      is_split: !!f.isSplit,
      f_tyres: f.front
        ? {
            desc: f.front,
            full_desc: f.front,
            matches: Array.isArray(f.frontMatches) ? f.frontMatches : [],
          }
        : null,
      r_tyres:
        f.isSplit && f.rear
          ? {
              desc: f.rear,
              full_desc: f.rear,
              matches: Array.isArray(f.rearMatches) ? f.rearMatches : [],
            }
          : undefined,
    }));
    return {
      id: result.vehicleId,
      vehicle_id: result.vehicleId,
      display_name: result.displayName || result.vehicleDescription || '',
      make: {
        display_name: result.make || '',
      },
      model: {
        display_name: result.model || '',
      },
      is_electric: !!result.isElectric,
      is_hybrid: !!result.isHybrid,
      rego: result.registration || null,
      regoState: result.state || null,
      fitmentInfo: {
        tyre_fitments: tyreFitments,
        wheel_fitments: [],
      },
      wheelAlignment: result.wheelAlignment && result.wheelAlignment.handle ? { handle: result.wheelAlignment.handle } : null,
    };
  }
  async #applyVehicleResult(result) {
    if (this.#chatSessionHasRecaptchaToken === false && this.#regoLookupCount >= AgentOverlayComponent.#MAX_REGO_LOOKUPS_WITHOUT_VERIFIED_SESSION) {
      this.#addMessage(
        'agent',
        "I've tried a few registration lookups already this conversation and can't verify it's really you right now - could you give me your tyre size directly instead (width / aspect ratio / rim diameter)?",
      );
      this.#saveState();
      return;
    }
    this.#regoLookupCount++;
    this.#saveState();
    if (result?.needsClientLookup) {
      await this.#performClientVehicleLookup(result.registration, result.state);
      return;
    }
    if (!result?.found) {
      this.#addMessage('agent', "I couldn't find a vehicle for that registration — could you double-check it, or give me the tyre size directly instead?");
      return;
    }
    const vehicleSearch = typeof window.vehicleSearch === 'function' ? window.vehicleSearch() : null;
    if (!vehicleSearch) {
      console.warn('vehicleSearch Pinia store not available - vehicle selection was not applied site-wide.');
      return;
    }
    const vehicle = this.#buildVehicleFromFindResult(result);
    if (vehicle.vehicle_id && typeof vehicleSearch.getFitment === 'function') {
      try {
        const realFitment = await vehicleSearch.getFitment(vehicle.vehicle_id);
        if (realFitment && realFitment !== 'fitFail' && Array.isArray(realFitment.tyre_fitments)) {
          vehicle.fitmentInfo = realFitment;
        }
      } catch (error) {
        console.warn('AI agent chat: real fitment lookup failed, falling back to chat-only fitment data.', error);
      }
    }
    try {
      await vehicleSearch.addVehicle(vehicle);
      window.dispatchEvent(
        new CustomEvent('vehicle-updated', {
          detail: vehicle,
        }),
      );
    } catch (error) {
      console.error('AI agent vehicle selection failed:', error);
      return;
    }
    const fitments = Array.isArray(result.fitments) ? result.fitments : [];
    if (fitments.length > 1) {
      this.#addFitmentBubbles(fitments);
    } else if (fitments.length === 1 && typeof window.fitmentStore === 'function') {
      const tyreFitments = vehicle.fitmentInfo.tyre_fitments;
      if (tyreFitments[0]) window.fitmentStore().setSelectedFitment(tyreFitments[0]);
    }
    if (fitments[0]) this.#searchForFitment(fitments[0]);
  }
  async #performClientVehicleLookup(registration, state) {
    const vehicleSearch = typeof window.vehicleSearch === 'function' ? window.vehicleSearch() : null;
    const hasWidget = document.getElementById('recaptcha') && typeof window.grecaptcha !== 'undefined';
    if (!vehicleSearch || !hasWidget) {
      this.#addMessage('agent', "Sorry, vehicle lookup isn't available right now — could you give me your tyre size directly instead?");
      return;
    }
    vehicleSearch.regoLPN = registration;
    vehicleSearch.regoState = state;
    let vehicleCall;
    try {
      vehicleCall = await new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          unsubscribe();
          reject(new Error('Vehicle lookup timed out'));
        }, 2e4);
        const unsubscribe = vehicleSearch.$subscribe((_mutation, storeState) => {
          const call = storeState.vehicleCall;
          if (call === 'regoFail') {
            clearTimeout(timeoutId);
            unsubscribe();
            resolve(null);
          } else if (call && typeof call === 'object' && call.status !== 'pending' && call.id) {
            clearTimeout(timeoutId);
            unsubscribe();
            resolve(call);
          }
        });
        window.grecaptcha.execute();
      });
    } catch (error) {
      console.error('AI agent vehicle lookup failed:', error);
      await this.#continueConversationWithContext(`[[VEHICLE_LOOKUP_RESULT: the lookup for registration ${registration} ${state} timed out or failed]]`);
      return;
    }
    if (!vehicleCall?.id) {
      await this.#continueConversationWithContext(`[[VEHICLE_LOOKUP_RESULT: no vehicle found for registration ${registration} ${state}]]`);
      return;
    }
    const fitmentData = await vehicleSearch.getFitment(vehicleCall.id).catch(() => null);
    const vehicleDescription = [vehicleCall?.make?.display_name, vehicleCall?.model?.display_name, vehicleCall?.display_name].filter(Boolean).join(' ');
    if (!fitmentData || fitmentData === 'fitFail') {
      await this.#continueConversationWithContext(`[[VEHICLE_LOOKUP_RESULT: found vehicle "${vehicleDescription}" but fitment data is unavailable right now]]`);
      return;
    }
    const vehicle = {
      id: vehicleCall.id,
      vehicle_id: vehicleCall.id,
      display_name: vehicleCall.display_name || '',
      make: vehicleCall.make || {},
      model: vehicleCall.model || {},
      is_electric: !!vehicleCall.is_electric,
      is_hybrid: !!vehicleCall.is_hybrid,
      rego: registration,
      regoState: state,
      fitmentInfo: fitmentData,
    };
    try {
      await vehicleSearch.addVehicle(vehicle);
      window.dispatchEvent(
        new CustomEvent('vehicle-updated', {
          detail: vehicle,
        }),
      );
    } catch (error) {
      console.error('AI agent vehicle selection failed:', error);
      return;
    }
    const fitments = this.#flattenTyreFitments(fitmentData?.tyre_fitments);
    if (fitments.length > 1) {
      this.#addFitmentBubbles(fitments);
    } else if (fitments[0] && typeof window.fitmentStore === 'function') {
      window.fitmentStore().setSelectedFitment(fitmentData.tyre_fitments[0]);
    }
    const standard = fitments[0];
    if (standard) this.#searchForFitment(standard);
    const alternateCount = Math.max(0, fitments.length - 1);
    // #searchForFitment above already fetched and rendered real products for
    // the standard fitment DIRECTLY (no agent round-trip) - without this,
    // the agent's own HARD RULE ("never name/describe a product without
    // also calling search_products") would call search_products again for
    // the exact same standard fitment a few seconds later, re-rendering the
    // identical grid. Telling it explicitly here that this one search
    // already ran (same pattern as the PERFORMANCE_PRIORITIES_SELECTED
    // marker elsewhere in this file) stops it
    // from repeating a search whose results are already on screen.
    await this.#continueConversationWithContext(`[[VEHICLE_LOOKUP_RESULT: found vehicle "${vehicleDescription}"; standard fitment ${standard?.isSplit ? `front ${standard.front}, rear ${standard.rear}` : standard?.front || 'unknown'}${alternateCount ? `; ${alternateCount} alternate fitment(s) also available` : ''}${standard ? ' - products for the STANDARD fitment are ALREADY shown on screen (a real search already ran) - do NOT call search_products again for this same size; only search again if the customer asks to narrow down, see an alternate fitment, or changes something' : ''}]]`);
  }
  #flattenTyreFitments(tyreFitments) {
    return (Array.isArray(tyreFitments) ? tyreFitments : [])
      .map((fitment, index) => {
        const front = fitment?.f_tyres?.full_desc || fitment?.f_tyres?.desc || null;
        const isSplit = !!fitment?.is_split;
        const rear = isSplit ? fitment?.r_tyres?.full_desc || fitment?.r_tyres?.desc || null : null;
        return {
          id: fitment?.id ?? null,
          label: index === 0 ? 'Standard fitment' : 'Alternate fitment',
          front: front,
          rear: rear,
          isSplit: isSplit,
          isOe: !!fitment?.is_oe,
        };
      })
      .filter((f) => f.front);
  }
  #parseSizeFromFitmentText(sizeText) {
    const match = String(sizeText || '').match(/(\d{3})\s*\/\s*(\d{2})\s*[A-Z]{0,3}\s*R\s*(\d{2})/i);
    if (!match) return null;
    return {
      width: match[1],
      aspectRatio: match[2],
      rimDiameter: match[3],
    };
  }
  #syncFitmentWithSearchedSize(searchedSize) {
    if (!searchedSize?.width || !searchedSize?.aspectRatio || !searchedSize?.rimDiameter) return;
    const { width, aspectRatio, rimDiameter } = searchedSize;
    try {
      localStorage.setItem('selectedSizeDisplay', `${width}/${aspectRatio}R${rimDiameter}`);
      localStorage.setItem('selectedWidth', width);
      localStorage.setItem('selectedAspectRatio', aspectRatio);
      localStorage.setItem('selectedDiameter', rimDiameter);
      localStorage.setItem(
        'searchWidgetFilters',
        JSON.stringify([
          { productMetafield: { namespace: 'wheel_tyre_spec', key: 'width', value: width } },
          { productMetafield: { namespace: 'tyre_spec', key: 'aspect_ratio', value: aspectRatio } },
          { productMetafield: { namespace: 'wheel_tyre_spec', key: 'rim_diameter', value: rimDiameter } },
        ])
      );
      localStorage.removeItem('activeVehicleId');
    } catch {
      return;
    }
    const tyresHandle = String(window.shopifyConfig?.tyresCollection || 'tyres');
    if (window.location.pathname === `/collections/${tyresHandle}`) {
      window.location.reload();
    }
  }
  async #searchForFitment(fitment) {
    const size = this.#parseSizeFromFitmentText(fitment?.front);
    if (!size || !this.#proxyUrl) return;
    const result = await this.#fetchBrowserSearch({
      ...size,
      mode: 'present',
    });
    if (result?.products?.length) {
      this.#renderProducts(result.products, result.filterSummary, result.pageFilters, result.requestedLabels);
    }
    if (Array.isArray(result?.pageFilters) && result.pageFilters.length) {
      try {
        localStorage.setItem(
          AgentOverlayComponent.#PENDING_COLLECTION_FILTERS_KEY,
          JSON.stringify({ filters: result.pageFilters, ts: Date.now() }),
        );
      } catch (error) {
        console.warn('AI agent chat: failed to arm collection-page fitment hand-off.', error);
      }
    }
  }
  async #continueConversationWithContext(hiddenContent, options = {}) {
    if (!this.#proxyUrl) return;
    await this.#realAgentReply(hiddenContent, null, options);
  }
  #addFitmentBubbles(fitments) {
    const { messages: messages } = this.refs;
    const container = document.createElement('div');
    container.className = 'agent-overlay__fitment-bubbles';
    for (const fitment of fitments) {
      const bubble = document.createElement('button');
      bubble.type = 'button';
      bubble.className = 'agent-overlay__fitment-bubble';
      const name = document.createElement('div');
      name.className = 'agent-overlay__fitment-bubble-name';
      name.textContent = fitment.label || 'Fitment';
      const size = document.createElement('div');
      size.className = 'agent-overlay__fitment-bubble-size';
      size.textContent = fitment.isSplit ? `Front ${fitment.front}, rear ${fitment.rear}` : fitment.front || '';
      bubble.append(name, size);
      bubble.addEventListener('click', async (event) => {
        event.stopPropagation();
        this.#selectFitmentOption(fitment);
        this.#saveState();
        this.#searchForFitment(fitment);
        const sizeDescription = fitment.isSplit ? `front ${fitment.front}, rear ${fitment.rear}` : fitment.front || 'unknown';
        await this.#continueConversationWithContext(`[[FITMENT_SELECTED: customer confirmed ${sizeDescription} is the fitment now on their car - proceed accordingly]]`);
      });
      container.appendChild(bubble);
    }
    messages.appendChild(container);
  }
  #showPerformancePicker(availableDimensions = null) {
    const dimensions = Array.isArray(availableDimensions)
      ? AgentOverlayComponent.#PERFORMANCE_DIMENSIONS.filter((d) => availableDimensions.includes(d.key))
      : AgentOverlayComponent.#PERFORMANCE_DIMENSIONS;
    const { messages: messages } = this.refs;
    const container = document.createElement('div');
    container.className = 'agent-overlay__performance-picker';
    const intro = document.createElement('p');
    intro.className = 'agent-overlay__performance-picker-intro';
    intro.textContent = `Pick ${AgentOverlayComponent.#PERFORMANCE_PICKER_REQUIRED_COUNT} things that matter most to you:`;
    const list = document.createElement('div');
    list.className = 'agent-overlay__performance-picker-list';
    const checkboxes = [];
    for (const dimension of dimensions) {
      const item = document.createElement('label');
      item.className = 'agent-overlay__performance-picker-item';
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.value = dimension.key;
      const label = document.createElement('span');
      label.className = 'agent-overlay__performance-picker-item-label';
      label.textContent = dimension.label;
      const desc = document.createElement('span');
      desc.className = 'agent-overlay__performance-picker-item-desc';
      desc.textContent = dimension.description;
      item.append(checkbox, label, desc);
      list.appendChild(item);
      checkboxes.push(checkbox);
    }
    const footer = document.createElement('div');
    footer.className = 'agent-overlay__performance-picker-footer';
    const count = document.createElement('span');
    count.className = 'agent-overlay__performance-picker-count';
    const doneButton = document.createElement('button');
    doneButton.type = 'button';
    doneButton.className = 'btnPrimary';
    doneButton.textContent = 'Done';
    doneButton.disabled = true;
    // On mobile the Done button is often below the fold once 3 boxes are
    // ticked, so committing shouldn't strictly depend on finding and
    // tapping it. Auto-commits 3s after the 3rd tick - cancelled if the
    // customer unchecks one before it fires, and manually clicking Done
    // still works immediately either way.
    const AUTO_COMMIT_DELAY_MS = 3000;
    let autoCommitTimer = null;
    let committed = false;
    const commitSelection = async () => {
      if (committed) return;
      committed = true;
      if (autoCommitTimer) {
        clearTimeout(autoCommitTimer);
        autoCommitTimer = null;
      }
      const selected = dimensions.filter((dimension) => checkboxes.find((c) => c.value === dimension.key)?.checked);
      for (const checkbox of checkboxes) checkbox.disabled = true;
      doneButton.disabled = true;
      doneButton.hidden = true;
      const summary = document.createElement('p');
      summary.className = 'agent-overlay__performance-picker-summary';
      summary.textContent = `You picked: ${selected.map((d) => d.label).join(', ')}`;
      container.appendChild(summary);
      const keys = selected.map((d) => d.key);
      await this.#applyPerformanceNarrowing(keys);
    };
    const updateCount = () => {
      const checkedCount = checkboxes.filter((c) => c.checked).length;
      const required = AgentOverlayComponent.#PERFORMANCE_PICKER_REQUIRED_COUNT;
      count.textContent = `${checkedCount} of ${required} selected`;
      doneButton.disabled = checkedCount !== required;
      for (const checkbox of checkboxes) {
        if (!checkbox.checked) checkbox.disabled = checkedCount >= required;
      }
      if (autoCommitTimer) {
        clearTimeout(autoCommitTimer);
        autoCommitTimer = null;
      }
      if (checkedCount === required) {
        autoCommitTimer = setTimeout(() => {
          autoCommitTimer = null;
          commitSelection();
        }, AUTO_COMMIT_DELAY_MS);
      }
    };
    for (const checkbox of checkboxes) checkbox.addEventListener('change', updateCount);
    updateCount();
    doneButton.addEventListener('click', () => {
      commitSelection();
    });
    footer.append(count, doneButton);
    container.append(intro, list, footer);
    messages.appendChild(container);
    messages.scrollTop = messages.scrollHeight;
  }
  #selectFitmentOption(fitment) {
    const vehicleSearch = typeof window.vehicleSearch === 'function' ? window.vehicleSearch() : null;
    const activeVehicle = vehicleSearch?.vehicles?.[0];
    const tyreFitments = activeVehicle?.fitmentInfo?.tyre_fitments;
    if (!Array.isArray(tyreFitments)) return;
    const index = tyreFitments.findIndex((f) => f?.id === fitment.id);
    if (index > 0) {
      const [selected] = tyreFitments.splice(index, 1);
      tyreFitments.unshift(selected);
    }
    vehicleSearch.updateTyreFitment(tyreFitments);
    try {
      localStorage.setItem('vehicles', JSON.stringify(vehicleSearch.vehicles));
    } catch (error) {
      console.warn('AI agent chat: failed to persist vehicle selection.', error);
    }
    if (typeof window.fitmentStore === 'function' && tyreFitments[0]) {
      window.fitmentStore().setSelectedFitment(tyreFitments[0]);
    }
    window.dispatchEvent(
      new CustomEvent('vehicle-updated', {
        detail: activeVehicle,
      }),
    );
  }
  async #renderProductResults(queue) {
    const isAllAxle = (entry, axle) => entry.view !== 'compare' && entry.products.length > 0 && entry.products.every((p) => p.axle === axle);
    if (queue.length === 2) {
      const frontEntry = queue.find((e) => isAllAxle(e, 'front'));
      const rearEntry = queue.find((e) => isAllAxle(e, 'rear'));
      if (frontEntry && rearEntry && frontEntry !== rearEntry) {
        await this.#renderSplitProducts(frontEntry, rearEntry);
        return;
      }
    }
    for (const entry of queue) {
      if (entry.view === 'compare') {
        this.#renderCompare(entry.products, entry.requestedLabels);
      } else {
        await this.#renderProducts(entry.products, entry.filterSummary, entry.pageFilters, entry.requestedLabels, entry.availabilityFilter, entry.searchedSize);
      }
    }
  }
  async #renderSplitProducts(frontEntry, rearEntry) {
    const { products: productsPane, productsFilterSummary: productsFilterSummary, productsCarousel: productsCarousel } = this.refs;
    const frontProducts = frontEntry.products;
    const rearProducts = rearEntry.products;
    const oeTagSetEqual = (a, b) => {
      const setA = new Set((Array.isArray(a) ? a : []).map((t) => String(t).toLowerCase()));
      const setB = new Set((Array.isArray(b) ? b : []).map((t) => String(t).toLowerCase()));
      if (setA.size !== setB.size) return false;
      for (const tag of setA) if (!setB.has(tag)) return false;
      return true;
    };
    const usedRear = new Set();
    const pairs = [];
    for (const front of frontProducts) {
      if (!front.pattern) continue;
      const rearIndex = rearProducts.findIndex(
        (r, i) => !usedRear.has(i) && r.pattern && r.pattern === front.pattern && oeTagSetEqual(front.oeLabelTags, r.oeLabelTags)
      );
      if (rearIndex === -1) continue;
      usedRear.add(rearIndex);
      pairs.push({ front, rear: rearProducts[rearIndex] });
    }
    const pairedFrontHandles = new Set(pairs.map((p) => p.front.handle));
    const pairedRearHandles = new Set(pairs.map((p) => p.rear.handle));
    const soloFront = frontProducts.filter((p) => !pairedFrontHandles.has(p.handle));
    const soloRear = rearProducts.filter((p) => !pairedRearHandles.has(p.handle));

    this.#lastProducts = [...frontProducts, ...rearProducts];
    this.#lastRenderIsSplit = true;
    this.#updateProductsPill();
    this.#lastRequestedLabels = Array.isArray(frontEntry.requestedLabels) ? frontEntry.requestedLabels : [];
    this.#lastRenderView = 'grid';
    this.#lastFilterSummary = [frontEntry.filterSummary, rearEntry.filterSummary].filter(Boolean).join(' · ') || null;
    this.#lastPageFilters = [];
    this.#lastAvailabilityFilter = null;

    productsPane.hidden = false;
    this.refs.panes.classList.remove('agent-overlay__panes--chat-expanded');
    this.refs.panes.classList.add('agent-overlay__panes--products-expanded');
    this.refs.productsLoading.hidden = false;
    productsCarousel.hidden = true;
    productsCarousel.replaceChildren();
    productsPane.querySelector('.agent-overlay__products-note')?.remove();
    productsFilterSummary.replaceChildren();
    if (this.#lastFilterSummary) {
      productsFilterSummary.hidden = false;
      productsFilterSummary.append('Front and rear sizes differ for this vehicle — ');
      const strong = document.createElement('strong');
      strong.textContent = this.#lastFilterSummary;
      productsFilterSummary.appendChild(strong);
    } else {
      productsFilterSummary.hidden = true;
    }
    this.#setNarrow(false);
    this.#renderedProductCards = [];
    for (const { front, rear } of pairs) {
      productsCarousel.appendChild(this.#buildSplitCard(front, rear));
    }
    for (const product of [...soloFront, ...soloRear]) {
      productsCarousel.appendChild(this.#buildSingleCard(product));
    }
    if (!pairs.length && !soloFront.length && !soloRear.length) {
      const note = document.createElement('p');
      note.className = 'agent-overlay__products-note';
      note.textContent = "Couldn't find any matching tyres for this vehicle's front and rear sizes.";
      (productsPane.querySelector('.agent-overlay__products-sheet-body') || productsPane).appendChild(note);
    }
    try {
      await Promise.race([this.#refreshStockForVisibleProducts(), new Promise((resolve) => setTimeout(resolve, 1e4))]);
    } finally {
      this.refs.productsLoading.hidden = true;
      productsCarousel.hidden = false;
    }
  }
  #buildSingleCard(product) {
    const productUrl = `/products/${product.handle}`;
    const card = document.createElement('div');
    card.className = 'productCard';
    const cardInner = document.createElement('div');
    cardInner.className = 'productCardInner';
    const media = document.createElement('div');
    media.className = 'productImage';
    const mediaLink = document.createElement('a');
    mediaLink.href = productUrl;
    if (product.image) {
      const img = document.createElement('img');
      img.src = product.image;
      img.loading = 'lazy';
      img.alt = product.title || '';
      mediaLink.appendChild(img);
    }
    media.appendChild(mediaLink);
    const promoRibbon = this.#buildPromoRibbon(product);
    if (promoRibbon) media.appendChild(promoRibbon);
    const info = document.createElement('div');
    info.className = 'productInfo';
    const vendorRow = document.createElement('div');
    vendorRow.className = 'productVendorRow';
    const vendor = document.createElement('div');
    vendor.className = 'productVendor';
    vendor.textContent = product.vendor || '';
    vendorRow.appendChild(vendor);
    if (product.oeMatch) {
      const oeBadge = document.createElement('div');
      oeBadge.className = 'agent-overlay__oe-badge';
      oeBadge.textContent = product.fitsVehicle ? `Made for your ${product.fitsVehicle}` : 'Made for your vehicle';
      vendorRow.appendChild(oeBadge);
    }
    const axleBadge = this.#buildAxleBadge(product);
    if (axleBadge) vendorRow.appendChild(axleBadge);
    const sizeMismatchBadge = this.#buildSizeMismatchBadge(product);
    if (sizeMismatchBadge) vendorRow.appendChild(sizeMismatchBadge);
    const title = document.createElement('h3');
    title.className = 'productTitle';
    const titleLink = document.createElement('a');
    titleLink.href = productUrl;
    titleLink.textContent = product.title || '';
    title.appendChild(titleLink);
    const matchScoreBadge = this.#buildMatchScoreBadge(product);
    if (matchScoreBadge) title.appendChild(matchScoreBadge);
    const spec = document.createElement('div');
    spec.className = 'productSpec';
    const stock = document.createElement('div');
    stock.className = 'stockStatus';
    const pricing = document.createElement('div');
    pricing.className = 'productPricing';
    const price = document.createElement('div');
    price.className = 'productPrice';
    price.textContent = this.#formatPrice(product.price);
    pricing.appendChild(price);
    const actions = document.createElement('div');
    actions.className = 'productActions1';
    const cartControls = this.#buildCartControls(product, stock);
    if (cartControls) actions.appendChild(cartControls);
    const lastCard = this.#renderedProductCards[this.#renderedProductCards.length - 1];
    if (lastCard && lastCard.product === product) lastCard.cardEl = card;
    const viewLink = document.createElement('a');
    viewLink.href = productUrl;
    viewLink.className = 'btnSecondary';
    viewLink.textContent = 'View Product';
    // Minimise the chat before navigating to the PDP - per an explicit
    // request, leaving it open just overlays the product the customer
    // asked to see. #close() persists isOpen=false so the chat doesn't
    // auto-reopen once #restoreState runs on the new page either.
    viewLink.addEventListener('click', () => this.close());
    actions.appendChild(viewLink);
    const performanceRow = this.#buildPerformanceMatchesRow(product);
    info.append(vendorRow);
    if (performanceRow) info.append(performanceRow);
    info.append(title, spec, stock, pricing, actions);
    cardInner.append(media, info);
    card.appendChild(cardInner);
    return card;
  }
  #buildSplitCard(front, rear) {
    const productUrl = `/products/${front.handle}`;
    const card = document.createElement('div');
    card.className = 'productCard agent-overlay__product-card--split';
    const cardInner = document.createElement('div');
    cardInner.className = 'productCardInner';
    const media = document.createElement('div');
    media.className = 'productImage';
    const mediaLink = document.createElement('a');
    mediaLink.href = productUrl;
    if (front.image) {
      const img = document.createElement('img');
      img.src = front.image;
      img.loading = 'lazy';
      img.alt = front.title || '';
      mediaLink.appendChild(img);
    }
    media.appendChild(mediaLink);
    const promoRibbon = this.#buildPromoRibbon(front);
    if (promoRibbon) media.appendChild(promoRibbon);
    const info = document.createElement('div');
    info.className = 'productInfo';
    const vendorRow = document.createElement('div');
    vendorRow.className = 'productVendorRow';
    const vendor = document.createElement('div');
    vendor.className = 'productVendor';
    vendor.textContent = front.vendor || '';
    vendorRow.appendChild(vendor);
    if (front.oeMatch || rear.oeMatch) {
      const oeBadge = document.createElement('div');
      oeBadge.className = 'agent-overlay__oe-badge';
      const fitsVehicle = front.fitsVehicle || rear.fitsVehicle;
      oeBadge.textContent = fitsVehicle ? `Made for your ${fitsVehicle}` : 'Made for your vehicle';
      vendorRow.appendChild(oeBadge);
    }
    const performanceRow = this.#buildPerformanceMatchesRow(front.performanceMatches?.length ? front : rear);
    const splitTitleParts = (titleA, titleB) => {
      const wordsA = String(titleA || '').trim().split(/\s+/).filter(Boolean);
      const wordsB = String(titleB || '').trim().split(/\s+/).filter(Boolean);
      let i = 0;
      while (i < wordsA.length && i < wordsB.length && wordsA[i].toLowerCase() === wordsB[i].toLowerCase()) {
        i += 1;
      }
      return {
        common: wordsA.slice(0, i).join(' '),
        restA: wordsA.slice(i).join(' '),
        restB: wordsB.slice(i).join(' '),
      };
    };
    const { common: sharedTitle, restA: frontRest, restB: rearRest } = splitTitleParts(front.title, rear.title);
    const title = document.createElement('h3');
    title.className = 'productTitle';
    const titleLink = document.createElement('a');
    titleLink.href = productUrl;
    titleLink.textContent = sharedTitle || front.title || '';
    title.appendChild(titleLink);
    const spec = document.createElement('div');
    spec.className = 'productSpec';
    const pricing = document.createElement('div');
    pricing.className = 'productPricing agent-overlay__split-pricing';
    const AXLE_QUANTITY = 2;
    const buildRow = (axleLabel, product, restText) => {
      const row = document.createElement('div');
      row.className = 'agent-overlay__split-row';
      const label = document.createElement('span');
      label.className = 'agent-overlay__split-label';
      label.textContent = `${axleLabel} (${AXLE_QUANTITY})${restText ? ` — ${restText}` : ''}`;
      const rowPrice = document.createElement('div');
      rowPrice.className = 'productPrice';
      rowPrice.textContent = this.#formatPrice(product.price);
      const stockEl = document.createElement('div');
      stockEl.className = 'stockStatus agent-overlay__split-stock';
      row.append(label, rowPrice, stockEl);
      return { row, stockEl };
    };
    const frontRow = buildRow('Front', front, frontRest);
    const rearRow = buildRow('Rear', rear, rearRest);
    pricing.append(frontRow.row, rearRow.row);
    const actions = document.createElement('div');
    actions.className = 'productActions1';
    const cartControls = document.createElement('div');
    cartControls.className = 'agent-overlay__cart-controls';
    const addToCartButton = document.createElement('button');
    addToCartButton.type = 'button';
    addToCartButton.className = 'btnPrimary';
    addToCartButton.textContent = 'Add to Cart';
    addToCartButton.addEventListener('click', () => {
      this.#addSplitToCart(front, AXLE_QUANTITY, rear, AXLE_QUANTITY, addToCartButton);
    });
    cartControls.append(addToCartButton);
    actions.appendChild(cartControls);
    const viewLink = document.createElement('a');
    viewLink.href = productUrl;
    viewLink.className = 'btnSecondary';
    viewLink.textContent = 'View Product';
    viewLink.addEventListener('click', () => this.close());
    actions.appendChild(viewLink);
    this.#renderedProductCards.push({
      product: front,
      splitProduct: rear,
      button: addToCartButton,
      stockEl: frontRow.stockEl,
      splitStockEl: rearRow.stockEl,
      cardEl: card,
    });
    info.append(vendorRow);
    if (performanceRow) info.append(performanceRow);
    info.append(title, spec, pricing, actions);
    cardInner.append(media, info);
    card.appendChild(cardInner);
    return card;
  }
  async #addSplitToCart(front, frontQty, rear, rearQty, button) {
    const cartTools = typeof window.cartTools === 'function' ? window.cartTools() : null;
    if (!cartTools) {
      this.#addMessage('agent', "Sorry, I can't add to cart from here right now — please add it from the product page instead.");
      return;
    }
    const storeSelector = typeof window.storeSelector === 'function' ? window.storeSelector() : null;
    const hasStore = !!storeSelector?.nearestStore || !!this.#safeLocalStorageGet('currentNearestStore');
    if (!hasStore) {
      this.#addMessage('agent', 'I just need your postcode or suburb first, so I can check stock at your nearest store before adding this to your cart.');
      return;
    }
    if (button.dataset.outOfStock === 'true') {
      this.#goToEnquiryPage();
      return;
    }
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    try {
      const vehicleSearch = typeof window.vehicleSearch === 'function' ? window.vehicleSearch() : null;
      let addedAny = false;
      for (const [product, quantity] of [
        [front, frontQty],
        [rear, rearQty],
      ]) {
        const variantId = this.#stripGid(product?.variantId);
        if (!variantId || !quantity) continue;
        const cartId = this.#getCartId(cartTools);
        const promo = this.#isFitmentFeeEligible(product);
        if (cartId) {
          await cartTools.cartAdd(variantId, quantity, cartId, promo);
        } else {
          await cartTools.newCart(variantId, quantity, vehicleSearch?.vehicles || [], storeSelector?.location, promo);
        }
        addedAny = true;
      }
      if (!addedAny) {
        button.disabled = false;
        button.textContent = originalText;
        this.#addMessage('agent', "Sorry, I couldn't find those exact products to add — please try from the product page.");
        return;
      }
      cartTools.openCheckout = false;
      cartTools.showCart = true;
      this.#updateCartBadge();
      this.#trackAgentAssistedCart(cartTools);
      button.textContent = 'Added!';
      this.close();
    } catch (error) {
      console.error('AI agent split add-to-cart failed:', error);
      button.disabled = false;
      button.textContent = originalText;
      this.#addMessage('agent', 'Sorry, something went wrong adding that to your cart — please try again.');
    }
  }
  async #renderProducts(products, filterSummary, pageFilters, requestedLabels, availabilityFilter, searchedSize = null) {
    // Temporary trace: a correctly-rendered grid has been observed getting
    // silently wiped moments later with no visible error anywhere. Logging
    // every call (count + a stack) lets us see the full call sequence and
    // find whatever's calling this a second time with an empty array.
    console.log('AI agent chat: #renderProducts called', { count: Array.isArray(products) ? products.length : products, stack: new Error().stack });
    const MAX_RENDERED_PRODUCTS = 24;
    this.#lastProducts = Array.isArray(products) ? products : [];
    this.#lastRenderIsSplit = false;
    this.#updateProductsPill();
    this.#lastRequestedLabels = Array.isArray(requestedLabels) ? requestedLabels : [];
    this.#lastRenderView = 'grid';
    this.#lastFilterSummary = typeof filterSummary === 'string' ? filterSummary : null;
    this.#lastPageFilters = Array.isArray(pageFilters) ? pageFilters : [];
    this.#lastAvailabilityFilter = typeof availabilityFilter === 'number' ? availabilityFilter : null;
    // Only overwrite when a real size is actually known this render - callers
    // that don't track it (a single-handle lookup, a brand/model quick-reply)
    // shouldn't blank out the last real search's size just by omitting it.
    if (searchedSize && searchedSize.width && searchedSize.aspectRatio && searchedSize.rimDiameter) {
      this.#lastSearchedSize = searchedSize;
    }
    this.#lastRenderSizeSignature = this.#getCurrentVehicleSizeSignature();
    const { products: productsPane, productsFilterSummary: productsFilterSummary, productsCarousel: productsCarousel } = this.refs;
    if (!this.#lastProducts.length) {
      productsPane.hidden = true;
      productsCarousel.replaceChildren();
      productsCarousel.hidden = true;
      productsFilterSummary.replaceChildren();
      productsFilterSummary.hidden = true;
      productsPane.querySelector('.agent-overlay__products-note')?.remove();
      this.refs.productsLoading.hidden = true;
      this.refs.panes.classList.remove('agent-overlay__panes--products-expanded');
      this.refs.panes.classList.add('agent-overlay__panes--chat-expanded');
      return;
    }
    productsPane.hidden = false;
    this.refs.panes.classList.remove('agent-overlay__panes--chat-expanded');
    this.refs.panes.classList.add('agent-overlay__panes--products-expanded');
    this.refs.productsLoading.hidden = false;
    productsCarousel.hidden = true;
    productsCarousel.replaceChildren();
    productsPane.querySelector('.agent-overlay__products-note')?.remove();
    productsFilterSummary.replaceChildren();
    if (filterSummary) {
      productsFilterSummary.hidden = false;
      productsFilterSummary.append('Filter criteria: ');
      const strong = document.createElement('strong');
      strong.textContent = filterSummary;
      productsFilterSummary.appendChild(strong);
      const searchLink = document.createElement('a');
      searchLink.className = 'agent-overlay__products-website-link';
      searchLink.textContent = 'See these tyres on the website';
      if (Array.isArray(pageFilters) && pageFilters.length) {
        searchLink.href = '/collections/tyres';
        searchLink.addEventListener('click', () => {
          try {
            localStorage.setItem(
              AgentOverlayComponent.#PENDING_COLLECTION_FILTERS_KEY,
              JSON.stringify({
                filters: pageFilters,
                ts: Date.now(),
              }),
            );
          } catch (error) {
            console.warn('AI agent chat: failed to hand off search context.', error);
          }
        });
      } else {
        searchLink.href = `/search?q=${encodeURIComponent(filterSummary.replace(/,/g, ' '))}`;
      }
      productsFilterSummary.append(document.createElement('br'), searchLink);
    } else {
      productsFilterSummary.hidden = true;
    }
    this.#setNarrow(false);
    const shown = products.slice(0, MAX_RENDERED_PRODUCTS);
    const isPerfectMatch = (p) => p.oeMatch === true || (typeof p.matchScoreTotal === 'number' && p.matchScoreTotal > 0 && p.matchScore === p.matchScoreTotal);
    const firstNonPerfectIndex = shown.findIndex((p) => typeof p.matchScoreTotal === 'number' && p.matchScoreTotal > 0 && !isPerfectMatch(p));
    const dividerIndex = firstNonPerfectIndex > 0 && shown.slice(0, firstNonPerfectIndex).some(isPerfectMatch) ? firstNonPerfectIndex : -1;
    const scoredProducts = shown.filter((p) => typeof p.matchScoreTotal === 'number' && p.matchScoreTotal > 0);
    const noExactMatch = scoredProducts.length > 0 && !scoredProducts.some(isPerfectMatch);
    if (noExactMatch) {
      const banner = document.createElement('div');
      banner.className = 'agent-overlay__no-exact-match-banner';
      const SVG_NS = 'http://www.w3.org/2000/svg';
      const icon = document.createElementNS(SVG_NS, 'svg');
      icon.setAttribute('viewBox', '0 0 24 24');
      icon.setAttribute('fill', 'currentColor');
      icon.setAttribute('aria-hidden', 'true');
      const iconPath = document.createElementNS(SVG_NS, 'path');
      iconPath.setAttribute('d', 'M12 2a10 10 0 100 20 10 10 0 000-20zm1 15h-2v-7h2zm0-9h-2V6h2z');
      icon.appendChild(iconPath);
      const text = document.createElement('span');
      text.textContent = "We couldn't find an exact match for everything you asked for — these are the closest tyres available.";
      banner.append(icon, text);
      productsCarousel.appendChild(banner);
    }
    this.#renderedProductCards = [];
    shown.forEach((product, index) => {
      if (index === dividerIndex) {
        const divider = document.createElement('div');
        divider.className = 'agent-overlay__match-divider';
        divider.textContent = 'Similar tyres — not perfect matches';
        productsCarousel.appendChild(divider);
      }
      if (product.splitPartner) {
        productsCarousel.appendChild(this.#buildSplitCard(product, product.splitPartner));
        return;
      }
      const productUrl = `/products/${product.handle}`;
      const card = document.createElement('div');
      card.className = 'productCard';
      const cardInner = document.createElement('div');
      cardInner.className = 'productCardInner';
      const media = document.createElement('div');
      media.className = 'productImage';
      const mediaLink = document.createElement('a');
      mediaLink.href = productUrl;
      if (product.image) {
        const img = document.createElement('img');
        img.src = product.image;
        img.loading = 'lazy';
        img.alt = product.title || '';
        mediaLink.appendChild(img);
      }
      media.appendChild(mediaLink);
      const promoRibbon = this.#buildPromoRibbon(product);
      if (promoRibbon) media.appendChild(promoRibbon);
      const info = document.createElement('div');
      info.className = 'productInfo';
      const vendorRow = document.createElement('div');
      vendorRow.className = 'productVendorRow';
      const vendor = document.createElement('div');
      vendor.className = 'productVendor';
      vendor.textContent = product.vendor || '';
      vendorRow.appendChild(vendor);
      if (product.recommended) {
        card.classList.add('agent-overlay__product-card--recommended');
        const badge = document.createElement('div');
        badge.className = 'agent-overlay__recommended-badge';
        badge.textContent = 'Recommended';
        vendorRow.appendChild(badge);
      }
      if (product.oeMatch) {
        const oeBadge = document.createElement('div');
        oeBadge.className = 'agent-overlay__oe-badge';
        oeBadge.textContent = product.fitsVehicle ? `Made for your ${product.fitsVehicle}` : 'Made for your vehicle';
        vendorRow.appendChild(oeBadge);
      }
      const axleBadge = this.#buildAxleBadge(product);
      if (axleBadge) vendorRow.appendChild(axleBadge);
      const sizeMismatchBadge = this.#buildSizeMismatchBadge(product);
      if (sizeMismatchBadge) vendorRow.appendChild(sizeMismatchBadge);
      const labelsRow = this.#buildLabelPills(product, requestedLabels);
      const title = document.createElement('h3');
      title.className = 'productTitle';
      const titleLink = document.createElement('a');
      titleLink.href = productUrl;
      titleLink.textContent = product.title || '';
      title.appendChild(titleLink);
      const matchScoreBadge = this.#buildMatchScoreBadge(product);
      if (matchScoreBadge) title.appendChild(matchScoreBadge);
      const spec = document.createElement('div');
      spec.className = 'productSpec';
      const stock = document.createElement('div');
      stock.className = 'stockStatus';
      const pricing = document.createElement('div');
      pricing.className = 'productPricing';
      const price = document.createElement('div');
      price.className = 'productPrice';
      price.textContent = this.#formatPrice(product.price);
      pricing.appendChild(price);
      let agentNoteEl = null;
      if (product.agentNote) {
        agentNoteEl = document.createElement('p');
        this.#fillProductNoteRibbon(agentNoteEl, product.agentNote);
      }
      const actions = document.createElement('div');
      actions.className = 'productActions1';
      const cartControls = this.#buildCartControls(product, stock);
      if (cartControls) actions.appendChild(cartControls);
      const lastCard = this.#renderedProductCards[this.#renderedProductCards.length - 1];
      if (lastCard && lastCard.product === product) lastCard.cardEl = card;
      const viewLink = document.createElement('a');
      viewLink.href = productUrl;
      viewLink.className = 'btnSecondary';
      viewLink.textContent = 'View Product';
      viewLink.addEventListener('click', () => this.close());
      actions.appendChild(viewLink);
      info.append(vendorRow);
      if (labelsRow) info.append(labelsRow);
      const performanceRow = this.#buildPerformanceMatchesRow(product);
      if (performanceRow) info.append(performanceRow);
      info.append(title, spec, stock, pricing);
      if (agentNoteEl) info.append(agentNoteEl);
      info.append(actions);
      cardInner.append(media, info);
      card.appendChild(cardInner);
      productsCarousel.appendChild(card);
    });
    if (products.length > shown.length) {
      const note = document.createElement('p');
      note.className = 'agent-overlay__products-note';
      note.textContent = 'More available — tell the agent more about what you need to narrow it down.';
      (productsPane.querySelector('.agent-overlay__products-sheet-body') || productsPane).appendChild(note);
    }
    try {
      await Promise.race([this.#refreshStockForVisibleProducts(availabilityFilter ?? null), new Promise((resolve) => setTimeout(resolve, 1e4))]);
    } finally {
      this.refs.productsLoading.hidden = true;
      productsCarousel.hidden = false;
    }
  }
  #renderCompare(products, requestedLabels) {
    this.#lastProducts = Array.isArray(products) ? products : [];
    this.#lastRenderIsSplit = false;
    this.#updateProductsPill();
    this.#lastRequestedLabels = Array.isArray(requestedLabels) ? requestedLabels : [];
    this.#lastRenderView = 'compare';
    const { products: productsPane, productsFilterSummary: productsFilterSummary, productsCarousel: productsCarousel } = this.refs;
    productsPane.hidden = false;
    this.refs.panes.classList.remove('agent-overlay__panes--chat-expanded');
    this.refs.panes.classList.add('agent-overlay__panes--products-expanded');
    productsCarousel.replaceChildren();
    productsPane.querySelector('.agent-overlay__products-note')?.remove();
    productsFilterSummary.replaceChildren();
    productsFilterSummary.hidden = true;
    this.#setNarrow(false);
    this.#renderedProductCards = [];
    if (!this.#lastProducts.length) {
      const note = document.createElement('p');
      note.className = 'agent-overlay__products-note';
      note.textContent = "Couldn't load those tyres for comparison — please try showing them again.";
      (productsPane.querySelector('.agent-overlay__products-sheet-body') || productsPane).appendChild(note);
      return;
    }
    const table = document.createElement('table');
    table.className = 'agent-overlay__compare-table';
    const colgroup = document.createElement('colgroup');
    const totalCols = products.length + 1;
    const colWidth = `${100 / totalCols}%`;
    for (let i = 0; i < totalCols; i++) {
      const col = document.createElement('col');
      col.style.width = colWidth;
      colgroup.appendChild(col);
    }
    table.appendChild(colgroup);
    table.style.minWidth = `${90 + products.length * 110}px`;
    const buildRow = (tagName, fill) => {
      const row = document.createElement('tr');
      const header = document.createElement('th');
      row.appendChild(header);
      for (const product of products) {
        const cell = document.createElement(tagName);
        if (product.recommended) cell.classList.add('agent-overlay__compare-cell--recommended');
        fill(cell, product);
        row.appendChild(cell);
      }
      return row;
    };
    const imageRow = buildRow('td', (cell, product) => {
      const imageWrap = document.createElement('div');
      imageWrap.className = 'agent-overlay__compare-image-wrap';
      const link = document.createElement('a');
      link.href = `/products/${product.handle}`;
      if (product.image) {
        const img = document.createElement('img');
        img.src = product.image;
        img.loading = 'lazy';
        img.alt = product.title || '';
        link.appendChild(img);
      }
      imageWrap.appendChild(link);
      cell.appendChild(imageWrap);
      if (product.recommended) {
        const badge = document.createElement('div');
        badge.className = 'agent-overlay__recommended-badge';
        badge.textContent = 'Recommended';
        cell.appendChild(badge);
      }
      if (product.oeMatch) {
        const oeBadge = document.createElement('div');
        oeBadge.className = 'agent-overlay__oe-badge agent-overlay__compare-chip--oe-match';
        oeBadge.textContent = product.fitsVehicle ? `Made for your ${product.fitsVehicle}` : 'Made for your vehicle';
        cell.appendChild(oeBadge);
      }
      const axleBadge = this.#buildAxleBadge(product);
      if (axleBadge) cell.appendChild(axleBadge);
      const sizeMismatchBadge = this.#buildSizeMismatchBadge(product);
      if (sizeMismatchBadge) cell.appendChild(sizeMismatchBadge);
    });
    const hasAnyPromo = products.some((p) => p?.promo && p?.promoType);
    const promoRow = hasAnyPromo
      ? buildRow('td', (cell, product) => {
          cell.classList.add('agent-overlay__compare-promo-cell');
          const ribbon = this.#buildPromoRibbon(product);
          if (ribbon) cell.appendChild(ribbon);
        })
      : null;
    if (promoRow) promoRow.firstElementChild.textContent = 'Promotion';
    const titleRow = buildRow('th', (cell, product) => {
      const vendor = document.createElement('div');
      vendor.className = 'productVendor';
      vendor.textContent = product.vendor || '';
      const title = document.createElement('div');
      title.className = 'productTitle';
      const titleLink = document.createElement('a');
      titleLink.href = `/products/${product.handle}`;
      titleLink.textContent = product.title || '';
      title.appendChild(titleLink);
      const matchScoreBadge = this.#buildMatchScoreBadge(product);
      if (matchScoreBadge) title.appendChild(matchScoreBadge);
      cell.append(vendor, title);
    });
    const allLabels = [...new Set([...(Array.isArray(requestedLabels) ? requestedLabels : []), ...products.flatMap((p) => (Array.isArray(p.labels) ? p.labels : []))])].sort((a, b) => a.localeCompare(b));
    const labelsRow = allLabels.length
      ? buildRow('td', (cell, product) => {
          const pills = this.#buildCompareLabelPills(product, allLabels, requestedLabels);
          cell.appendChild(pills);
        })
      : null;
    if (labelsRow) labelsRow.firstElementChild.textContent = 'Features';
    const fitsVehicleLabel = products.find((p) => p.fitsVehicle)?.fitsVehicle || null;
    const hasLabel = (product, label) => Array.isArray(product.labels) && product.labels.includes(label);
    const specDefs = [
      {
        label: 'Price',
        value: (p) => this.#formatPrice(p.price),
      },
      {
        label: 'Size',
        value: (p) => {
          const size = [p.width, p.aspectRatio].filter(Boolean).join('/') + (p.rimDiameter ? `R${p.rimDiameter}` : '');
          return size || '—';
        },
      },
      {
        label: 'Driving style',
        value: (p) => p.drivingStyle || '—',
      },
      {
        label: 'Load index',
        value: (p) => p.loadIndex || '—',
      },
      {
        label: 'Speed rating',
        value: (p) => {
          if (!p.speedRating) return '—';
          const maxSpeed = this.#describeMaxSpeed(p.speedRating);
          return maxSpeed !== '—' ? `${p.speedRating} / ${maxSpeed}` : p.speedRating;
        },
      },
      {
        label: 'Extra Load (XL)',
        value: (p) => (hasLabel(p, 'Extra Load') ? 'Yes' : 'No'),
      },
      {
        label: 'Eco Friendly',
        value: (p) => (hasLabel(p, 'Eco Friendly') ? 'Yes' : 'No'),
      },
      {
        label: 'Promotion',
        value: (p) => this.#describePromo(p) || '—',
      },
    ];
    if (fitsVehicleLabel && products.some((p) => p.oeMatch)) {
      specDefs.push({
        label: `Fits your ${fitsVehicleLabel}`,
        value: (p) => (p.oeMatch ? 'Yes' : 'No'),
        oeMatch: true,
      });
    }
    const specEntries = specDefs.map((def) => ({
      def: def,
      values: products.map((p) => def.value(p)),
    }));
    const commonEntries = specEntries.filter((e) => new Set(e.values).size === 1 && e.values[0] !== '—');
    const diffEntries = specEntries.filter((e) => new Set(e.values).size > 1);
    const commonRow = commonEntries.length
      ? (() => {
          const row = document.createElement('tr');
          const header = document.createElement('th');
          header.textContent = 'Common specs';
          row.appendChild(header);
          const cell = document.createElement('td');
          cell.colSpan = products.length;
          cell.className = 'agent-overlay__compare-common-cell';
          const list = document.createElement('div');
          list.className = 'agent-overlay__compare-common-list';
          for (const { def: def, values: values } of commonEntries) {
            const chip = document.createElement('span');
            if (def.oeMatch && values[0] === 'Yes') {
              chip.className = 'agent-overlay__compare-chip--oe-match';
              chip.textContent = `Made for your ${fitsVehicleLabel}`;
            } else {
              chip.className = 'agent-overlay__compare-common-chip';
              chip.textContent = `${def.label}: ${values[0]}`;
            }
            list.appendChild(chip);
          }
          cell.appendChild(list);
          row.appendChild(cell);
          return row;
        })()
      : null;
    const specRows = diffEntries.map(({ def: def }) => {
      const row = buildRow('td', (cell, product) => {
        if (def.oeMatch && product.oeMatch) {
          const pill = document.createElement('span');
          pill.className = 'agent-overlay__compare-chip--oe-match';
          pill.textContent = `Made for your ${fitsVehicleLabel}`;
          cell.appendChild(pill);
        } else {
          cell.textContent = def.value(product);
        }
      });
      row.firstElementChild.textContent = def.label;
      return row;
    });
    const stockCellByProduct = new Map();
    const stockRow = buildRow('td', (cell, product) => {
      stockCellByProduct.set(product, cell);
    });
    stockRow.firstElementChild.textContent = 'Stock';
    const anyNotes = products.some((p) => p.agentNote);
    const notesRow = anyNotes
      ? buildRow('td', (cell, product) => {
          cell.classList.add('agent-overlay__compare-cell--note');
          if (product.agentNote) {
            const wrapper = document.createElement('div');
            cell.appendChild(wrapper);
            this.#fillProductNoteRibbon(wrapper, product.agentNote);
          } else {
            cell.textContent = '—';
          }
        })
      : null;
    if (notesRow) notesRow.firstElementChild.textContent = 'Why this one';
    const actionsRow = buildRow('td', (cell, product) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'agent-overlay__compare-actions';
      const cartControls = this.#buildCartControls(product, stockCellByProduct.get(product) || null);
      if (cartControls) wrapper.appendChild(cartControls);
      const viewLink = document.createElement('a');
      viewLink.href = `/products/${product.handle}`;
      viewLink.className = 'btnSecondary';
      viewLink.textContent = 'View Product';
      viewLink.addEventListener('click', () => this.close());
      wrapper.appendChild(viewLink);
      cell.appendChild(wrapper);
    });
    table.append(imageRow);
    if (promoRow) table.append(promoRow);
    table.append(titleRow);
    if (commonRow) table.append(commonRow);
    if (labelsRow) table.append(labelsRow);
    table.append(...specRows, stockRow);
    if (notesRow) table.append(notesRow);
    table.append(actionsRow);
    const tableWrapper = document.createElement('div');
    tableWrapper.className = 'agent-overlay__compare-table-wrapper';
    tableWrapper.appendChild(table);
    productsCarousel.appendChild(tableWrapper);
    this.#refreshStockForVisibleProducts();
  }
  #buildCartControls(product, stockEl) {
    if (!product.variantId) return null;
    const cartControls = document.createElement('div');
    cartControls.className = 'agent-overlay__cart-controls';
    const qtyInput = document.createElement('input');
    qtyInput.type = 'number';
    qtyInput.className = 'agent-overlay__cart-qty';
    if (product.isUpsellService) {
      qtyInput.min = '1';
      qtyInput.max = '1';
      qtyInput.value = '1';
      qtyInput.disabled = true;
    } else {
      qtyInput.min = '1';
      qtyInput.max = '8';
      qtyInput.value = '4';
    }
    qtyInput.setAttribute('aria-label', 'Quantity');
    const addToCartButton = document.createElement('button');
    addToCartButton.type = 'button';
    addToCartButton.className = 'btnPrimary';
    addToCartButton.textContent = 'Add to Cart';
    addToCartButton.addEventListener('click', () => {
      if (addToCartButton.dataset.outOfStock === 'true') {
        this.#goToEnquiryPage();
        return;
      }
      const quantity = Math.min(8, Math.max(1, parseInt(qtyInput.value, 10) || 4));
      this.#addToCart(product, quantity, addToCartButton);
    });
    cartControls.append(qtyInput, addToCartButton);
    this.#renderedProductCards.push({
      product: product,
      button: addToCartButton,
      stockEl: stockEl || null,
    });
    return cartControls;
  }
  #formatPrice(amount) {
    const value = Number(amount);
    if (Number.isNaN(value)) return '';
    const formatted = new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(value);
    return formatted.replace(/\.00$/, '').replace(/\.0$/, '');
  }
  #findKnownProductByTitle(text) {
    const normalized = text.trim().toLowerCase();
    if (!normalized) return null;
    return this.#lastProducts.find((p) => typeof p?.title === 'string' && p.title.trim().toLowerCase() === normalized) || null;
  }
  #findKnownProductsByVendor(text) {
    const normalized = text.trim().toLowerCase();
    if (!normalized) return [];
    return this.#lastProducts.filter((p) => typeof p?.vendor === 'string' && p.vendor.trim().toLowerCase() === normalized);
  }
  #describePromo(product) {
    if (product?.buy3Get4Pricing) return 'Buy 3 Get 4';
    return product?.promoType || null;
  }
  #buildPromoRibbon(product) {
    if (!product?.promo || !product?.promoType) return null;
    const ribbon = document.createElement('div');
    ribbon.className = 'agent-overlay__promo-ribbon';
    const typeLower = String(product.promoType).toLowerCase();
    if (typeLower === 'buy 3 get 4') ribbon.classList.add('agent-overlay__promo-ribbon--b3g1');
    else if (typeLower === 'cash back') ribbon.classList.add('agent-overlay__promo-ribbon--cashback');
    else if (typeLower === 'percentage') ribbon.classList.add('agent-overlay__promo-ribbon--percent');
    if (product.promoColour) ribbon.style.backgroundColor = product.promoColour;
    ribbon.textContent = product.promoTitle || product.promoType;
    return ribbon;
  }
  #buildPerformanceMatchesRow(product) {
    const matches = Array.isArray(product?.performanceMatches) ? product.performanceMatches : [];
    if (!matches.length) return null;
    const row = document.createElement('div');
    row.className = 'productLabels';
    for (const match of matches) {
      const badge = document.createElement('span');
      badge.className = 'productLabelBadge';
      badge.classList.add(match.met ? 'agent-overlay__label-badge--matched' : 'agent-overlay__label-badge--missing');
      badge.textContent = match.label;
      row.appendChild(badge);
    }
    return row;
  }
  #buildLabelPills(product, requestedLabels) {
    const productLabels = Array.isArray(product?.labels) ? product.labels : [];
    const requested = product?.isUpsellService ? [] : Array.isArray(requestedLabels) ? requestedLabels : [];
    const union = [...new Set([...requested, ...productLabels])];
    if (!union.length) return null;
    const row = document.createElement('div');
    row.className = 'productLabels';
    for (const label of union) {
      const has = productLabels.includes(label);
      const wasRequested = requested.includes(label);
      const badge = document.createElement('span');
      badge.className = 'productLabelBadge';
      if (wasRequested && has) badge.classList.add('agent-overlay__label-badge--matched');
      else if (wasRequested && !has) badge.classList.add('agent-overlay__label-badge--missing');
      badge.textContent = label;
      row.appendChild(badge);
    }
    return row;
  }
  #buildCompareLabelPills(product, allLabels, requestedLabels) {
    const productLabels = new Set(Array.isArray(product?.labels) ? product.labels : []);
    const requested = new Set(Array.isArray(requestedLabels) ? requestedLabels : []);
    const row = document.createElement('div');
    row.className = 'productLabels';
    for (const label of allLabels) {
      const has = productLabels.has(label);
      const badge = document.createElement('span');
      badge.className = 'productLabelBadge';
      if (has && requested.has(label)) badge.classList.add('agent-overlay__label-badge--matched');
      else if (!has) badge.classList.add('agent-overlay__label-badge--missing');
      badge.textContent = label;
      row.appendChild(badge);
    }
    return row;
  }
  #describeMaxSpeed(speedRating) {
    if (!speedRating) return '—';
    const KMH_BY_SPEED_RATING = {
      B: 50,
      C: 60,
      D: 65,
      E: 70,
      F: 80,
      G: 90,
      J: 100,
      K: 110,
      L: 120,
      M: 130,
      N: 140,
      P: 150,
      Q: 160,
      R: 170,
      S: 180,
      T: 190,
      U: 200,
      H: 210,
      V: 240,
      W: 270,
      Y: 300,
      Z: 240,
    };
    const kmh = KMH_BY_SPEED_RATING[String(speedRating).trim().toUpperCase()];
    return kmh ? `${kmh} km/h` : '—';
  }
  #buildMatchScoreBadge(product) {
    const isOeMatch = product?.oeMatch === true;
    if (!isOeMatch && !product?.matchScoreTotal) return null;
    const percent = isOeMatch ? 100 : Math.round((Number(product.matchScore) / Number(product.matchScoreTotal)) * 100);
    const badge = document.createElement('span');
    badge.className = 'agent-overlay__match-score-badge';
    badge.classList.add(percent >= 100 ? 'agent-overlay__match-score-badge--full' : percent > 0 ? 'agent-overlay__match-score-badge--partial' : 'agent-overlay__match-score-badge--none');
    badge.textContent = `${percent}% Match`;
    return badge;
  }
  #buildAxleBadge(product) {
    if (product?.axle !== 'front' && product?.axle !== 'rear') return null;
    const badge = document.createElement('span');
    badge.className = `agent-overlay__axle-badge agent-overlay__axle-badge--${product.axle}`;
    badge.textContent = product.axle === 'front' ? 'Front' : 'Rear';
    return badge;
  }
  #getActiveVehicleFitmentSizes() {
    try {
      const vehicleSearch = typeof window.vehicleSearch === 'function' ? window.vehicleSearch() : null;
      const activeVehicleId = vehicleSearch?.activeVehicleId;
      const activeVehicle =
        activeVehicleId && Array.isArray(vehicleSearch?.vehicles)
          ? vehicleSearch.vehicles.find((v) => String(v?.id) === String(activeVehicleId) || String(v?.vehicle_id) === String(activeVehicleId))
          : null;
      const tyreFitments = activeVehicle?.fitmentInfo?.tyre_fitments;
      if (!activeVehicle || !Array.isArray(tyreFitments) || !tyreFitments.length) return null;
      const sizes = new Set();
      for (const fitment of this.#flattenTyreFitments(tyreFitments)) {
        const front = this.#parseSizeFromFitmentText(fitment.front);
        if (front) sizes.add(`${front.width}/${front.aspectRatio}/${front.rimDiameter}`);
        if (fitment.isSplit) {
          const rear = this.#parseSizeFromFitmentText(fitment.rear);
          if (rear) sizes.add(`${rear.width}/${rear.aspectRatio}/${rear.rimDiameter}`);
        }
      }
      if (!sizes.size) return null;
      return { modelLabel: activeVehicle.model?.display_name || activeVehicle.display_name || null, sizes };
    } catch (error) {
      console.warn('AI agent chat: skipped vehicle fitment size check.', error);
      return null;
    }
  }
  #buildSizeMismatchBadge(product) {
    if (!product?.width || !product?.aspectRatio || !product?.rimDiameter) return null;
    const context = this.#getActiveVehicleFitmentSizes();
    if (!context) return null;
    const key = `${product.width}/${product.aspectRatio}/${product.rimDiameter}`;
    if (context.sizes.has(key)) return null;
    const badge = document.createElement('span');
    badge.className = 'agent-overlay__size-mismatch-badge';
    badge.textContent = context.modelLabel ? `May not fit your ${context.modelLabel}` : 'May not fit your vehicle';
    return badge;
  }
  #fillProductNoteRibbon(container, text) {
    container.classList.add('agent-overlay__product-note');
    const SVG_NS = 'http://www.w3.org/2000/svg';
    const icon = document.createElementNS(SVG_NS, 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('fill', 'currentColor');
    icon.setAttribute('aria-hidden', 'true');
    const iconPath = document.createElementNS(SVG_NS, 'path');
    iconPath.setAttribute('d', 'M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8L12 2z');
    icon.appendChild(iconPath);
    const textSpan = document.createElement('span');
    textSpan.textContent = text;
    container.append(icon, textSpan);
  }
  async #loadProductForQuickReply(optionText, matchedProduct) {
    if (matchedProduct) {
      this.#renderProducts([matchedProduct], `"${matchedProduct.title}"`, [], this.#lastRequestedLabels);
      return;
    }
    const knownSize = this.#lastProducts.find((p) => p?.width && p?.aspectRatio && p?.rimDiameter);
    const matchesKnownSize = (list) =>
      !knownSize
        ? list
        : list.filter((p) => p?.width === knownSize.width && p?.aspectRatio === knownSize.aspectRatio && p?.rimDiameter === knownSize.rimDiameter);
    const sizeMismatchContext = (optionLabel) => {
      const sizeLabel = `${knownSize.width}/${knownSize.aspectRatio}R${knownSize.rimDiameter}`;
      return `[[SIZE_MISMATCH: "${optionLabel}" exists but not in the customer's current size (${sizeLabel}) - do NOT show or imply it fits; say plainly it's not available in that size and ask if they want a different size checked or a similar alternative in ${sizeLabel} instead]]`;
    };

    const rawVendorMatches = this.#findKnownProductsByVendor(optionText);
    const vendorMatches = matchesKnownSize(rawVendorMatches);
    if (vendorMatches.length) {
      this.#renderProducts(vendorMatches, optionText, [], this.#lastRequestedLabels);
      return;
    }
    if (rawVendorMatches.length && knownSize) {
      await this.#continueConversationWithContext(sizeMismatchContext(optionText));
      return;
    }
    if (!this.#proxyUrl) return;
    try {
      const [byBrand, byModel] = await Promise.all([
        this.#fetchBrowserSearch({
          brand: optionText,
          mode: 'present',
        }),
        this.#fetchBrowserSearch({
          modelQuery: optionText,
          mode: 'present',
        }),
      ]);
      const brandMatches = matchesKnownSize(byBrand?.products || []);
      const modelMatches = matchesKnownSize(byModel?.products || []);
      if (brandMatches.length) {
        this.#renderProducts(brandMatches, byBrand.filterSummary, byBrand.pageFilters, byBrand.requestedLabels);
      } else if (modelMatches.length) {
        this.#renderProducts(modelMatches, byModel.filterSummary, byModel.pageFilters, byModel.requestedLabels);
      } else if (knownSize && (byBrand?.products?.length || byModel?.products?.length)) {
        await this.#continueConversationWithContext(sizeMismatchContext(optionText));
      }
    } catch (error) {
      console.error('AI agent quick-reply product lookup failed:', error);
    }
  }
  async #fetchBrowserSearch(args) {
    try {
      const response = await fetch(`${this.#proxyUrl}/browser/search-products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(args),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }
  /**
   * Retell's own relay of a search_products tool_call_result can arrive
   * with its `products` array - and sometimes everything except `mode` -
   * stripped out entirely (Retell caps custom function results at 15,000
   * characters; a present-mode search_products response for a normal
   * result set routinely runs well over double that). The agent's reply
   * still reads out real results because Retell itself had the full data
   * when generating it - only the structured copy handed back to the
   * browser is affected.
   *
   * tool_call_invocation.arguments, by contrast, survives intact - it's the
   * agent's own (comparatively tiny) function call, not the large result.
   * Re-running that exact same search directly
   * against the proxy (the same /browser/search-products route the
   * performance-picker's #applyPerformanceNarrowing already uses, bypassing
   * Retell entirely) gets the real, complete data regardless of what the
   * relayed tool_call_result contains - falling back to parsing that
   * result's own content only if the direct re-fetch itself fails.
   */
  async #fetchDirectSearchResult(args, fallbackContent) {
    const direct = await this.#fetchBrowserSearch(args);
    const parsedDirect = direct ? this.#parseToolResult(direct) : null;
    if (parsedDirect) return parsedDirect;
    console.warn('AI agent chat: direct re-fetch of search_products failed - falling back to the (possibly truncated) relayed tool result.', args);
    return this.#parseToolResult(fallbackContent);
  }
  // The performance picker's own "Done" click is a deterministic UI event
  // this theme fully controls - unlike free-text chat, there's no reason
  // to leave the actual re-render up to the agent deciding to call
  // search_products (unreliable even with a "you MUST call this" marker).
  // Runs the narrowed search directly and renders it here, then tells the
  // agent what's already on screen instead of asking it to produce that
  // screen itself. Reapplies the last known size (#lastSearchedSize) and,
  // best-effort, the last pageFilters (e.g. an active brand filter) - if
  // the backend doesn't honor pageFilters as an input here, worst case
  // it's just an ignored extra field and the narrowing still applies
  // correctly to size + performance alone.
  async #applyPerformanceNarrowing(keys) {
    const size = this.#lastSearchedSize || {};
    const args = { ...size, performance: keys, mode: 'present' };
    if (this.#lastPageFilters?.length) args.pageFilters = this.#lastPageFilters;
    const result = await this.#fetchBrowserSearch(args);
    if (result?.products?.length) {
      await this.#renderProducts(result.products, result.filterSummary, result.pageFilters, result.requestedLabels, result.availabilityFilter, result.searchedSize || (size.width ? size : null));
      const summaryNote = result.filterSummary ? ` (${result.filterSummary})` : '';
      // result.performanceOverallNote (routes/products.js) is the explicit
      // "none of these fully match" statement, when true - must be forwarded
      // here rather than left for the agent to infer from performanceMatches
      // alone, since leaving it implicit lets the agent claim a full match
      // the data doesn't actually support.
      const overallNote = result.performanceOverallNote ? ` ${result.performanceOverallNote}` : '';
      await this.#continueConversationWithContext(
        `[[PERFORMANCE_PRIORITIES_SELECTED: ${JSON.stringify(keys)} - customer chose these ${keys.length} performance priorities from the checkbox picker (in this order). The matching results are ALREADY shown on screen${summaryNote} - this search has already run, do NOT call search_products again for it. Describe what's on screen using each product's own performanceSummary/performanceMatches - never invent your own read of whether it meets these priorities.${overallNote}]]`,
      );
      return;
    }
    // Direct narrowing failed or returned nothing - fall back to asking the
    // agent to do it itself rather than leaving the customer stuck.
    await this.#continueConversationWithContext(
      `[[PERFORMANCE_PRIORITIES_SELECTED: ${JSON.stringify(keys)} - customer chose these ${keys.length} performance priorities from the checkbox picker (in this order). A direct narrowing attempt just failed - you MUST call search_products THIS SAME TURN with performance: ${JSON.stringify(keys)} plus everything else already known (size, brand, driving style, etc.), mode "present". Never describe narrowed results in words without this call happening first.]]`,
    );
  }
  async #addToCart(product, quantity, button) {
    const cartTools = typeof window.cartTools === 'function' ? window.cartTools() : null;
    if (!cartTools) {
      this.#addMessage('agent', "Sorry, I can't add to cart from here right now — please add it from the product page instead.");
      return;
    }
    const storeSelector = typeof window.storeSelector === 'function' ? window.storeSelector() : null;
    const hasStore = !!storeSelector?.nearestStore || !!this.#safeLocalStorageGet('currentNearestStore');
    if (!hasStore) {
      this.#addMessage('agent', 'I just need your postcode or suburb first, so I can check stock at your nearest store before adding this to your cart.');
      return;
    }
    const variantId = this.#stripGid(product?.variantId);
    if (!variantId) {
      this.#addMessage('agent', "Sorry, I couldn't find that exact product to add — please try from the product page.");
      return;
    }
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    try {
      const vehicleSearch = typeof window.vehicleSearch === 'function' ? window.vehicleSearch() : null;
      const cartId = this.#getCartId(cartTools);
      const promo = this.#isFitmentFeeEligible(product);
      if (cartId) {
        await cartTools.cartAdd(variantId, quantity, cartId, promo);
      } else {
        await cartTools.newCart(variantId, quantity, vehicleSearch?.vehicles || [], storeSelector?.location, promo);
      }
      cartTools.openCheckout = false;
      cartTools.showCart = true;
      this.#updateCartBadge();
      this.#trackAgentAssistedCart(cartTools);
      button.textContent = 'Added!';
      this.close();
    } catch (error) {
      console.error('AI agent add-to-cart failed:', error);
      button.disabled = false;
      button.textContent = originalText;
      this.#addMessage('agent', 'Sorry, something went wrong adding that to your cart — please try again.');
    }
  }
  #isFitmentFeeEligible(product) {
    const collections = Array.isArray(product?.promoCollections) ? product.promoCollections : [];
    if (!collections.some((c) => c?.type === 'Buy 3 Get 4')) return false;
    const exemptionCollectionId = window.shopifyConfig?.fitmentFeeExemptionCollection;
    if (!exemptionCollectionId) return true;
    return !collections.some((c) => c?.id === `gid://shopify/Collection/${exemptionCollectionId}`);
  }
  async #refreshStockForVisibleProducts(availabilityFilter = null, overrideCandidates = null) {
    const cards = this.#renderedProductCards;
    if (!cards.length) return;
    const storeSelector = typeof window.storeSelector === 'function' ? window.storeSelector() : null;
    let nearestStore = storeSelector?.nearestStore || null;
    if (!nearestStore) {
      const raw = this.#safeLocalStorageGet('currentNearestStore');
      try {
        nearestStore = raw ? JSON.parse(raw) : null;
      } catch {
        nearestStore = null;
      }
    }
    const nearestLocationId = this.#stripGid(nearestStore?.node?.id);
    const hasOverride = Array.isArray(overrideCandidates) && overrideCandidates.length > 0;
    if (!nearestLocationId && !hasOverride) {
      if (availabilityFilter != null) {
        await this.#continueConversationWithContext('[[AVAILABILITY_CHECK: cannot check real availability yet - no store is selected. Ask the customer for their postcode or suburb first, then search again.]]');
      }
      return;
    }
    const stockTools = typeof window.stock === 'function' ? window.stock() : null;
    if (!stockTools) return;
    const productIds = [
      ...cards.map((card) => this.#stripGid(card.product?.productId)),
      ...cards.map((card) => this.#stripGid(card.splitProduct?.productId)),
    ].filter(Boolean);
    if (!productIds.length) return;
    for (const card of cards) {
      card.button.disabled = true;
      card.button.textContent = 'Checking stock…';
      if (card.stockEl) card.stockEl.textContent = 'Checking stock…';
      if (card.splitStockEl) card.splitStockEl.textContent = 'Checking stock…';
    }
    let customerLocation = storeSelector?.location || null;
    if (!customerLocation?.latitude) {
      const raw = this.#safeLocalStorageGet('currentUserLocation');
      try {
        customerLocation = raw ? JSON.parse(raw) : null;
      } catch {
        customerLocation = null;
      }
    }
    const customerLat = Number(customerLocation?.latitude);
    const customerLng = Number(customerLocation?.longitude);
    const hasCustomerLocation = Number.isFinite(customerLat) && Number.isFinite(customerLng);
    const distanceToStore = (address) => {
      if (!hasCustomerLocation || !storeSelector) return null;
      const lat = Number(address?.latitude);
      const lng = Number(address?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      const km = storeSelector.haversineFormula(customerLat, customerLng, lat, lng);
      return Number.isFinite(km) ? Math.round(km * 10) / 10 : null;
    };
    let nearbyLocations;
    if (hasOverride) {
      nearbyLocations = overrideCandidates.slice(0, 5).map((store) => ({
        id: this.#stripGid(String(store.id)),
        name: store.name || null,
        distanceKm: typeof store.distanceKm === 'number' ? store.distanceKm : distanceToStore({ latitude: store.latitude, longitude: store.longitude }),
        rawNode: {
          id: store.id,
          name: store.name,
          address: { formatted: store.address, phone: store.phone, latitude: store.latitude, longitude: store.longitude, provinceCode: store.provinceCode },
        },
      }));
    } else {
      nearbyLocations = [
        {
          id: nearestLocationId,
          name: nearestStore?.node?.name || null,
          distanceKm: distanceToStore(nearestStore?.node?.address),
          rawNode: nearestStore?.node || null,
        },
      ];
      try {
        const raw = this.#safeLocalStorageGet('storesUnder100');
        const parsed = raw ? JSON.parse(raw) : [];
        for (const entry of Array.isArray(parsed) ? parsed : []) {
          const id = this.#stripGid(entry?.node?.id);
          if (!id || id === nearestLocationId || nearbyLocations.some((l) => l.id === id)) continue;
          nearbyLocations.push({
            id: id,
            name: entry?.node?.name || null,
            distanceKm: distanceToStore(entry?.node?.address),
            rawNode: entry?.node || null,
          });
          if (nearbyLocations.length >= 5) break;
        }
      } catch (error) {
        console.warn('AI agent chat: skipped nearby-stores stock check.', error);
      }
    }
    let stockByProductId = {};
    try {
      const response = await stockTools.requestStock(
        productIds,
        nearbyLocations.map((l) => l.id),
        1,
        true,
      );
      for (const entry of Array.isArray(response) ? response : []) {
        const locations = Array.isArray(entry?.locations) ? entry.locations : [];
        let match = null;
        for (let i = 0; i < nearbyLocations.length; i++) {
          const normalized = String(locations[i]?.available ?? '')
            .trim()
            .toUpperCase();
          if (normalized === 'Y' || normalized === 'L') {
            match = {
              index: i,
              location: locations[i],
            };
            break;
          }
        }
        const chosenIndex = match ? match.index : 0;
        const chosenLocation = match ? match.location : locations[0];
        const normalized = String(chosenLocation?.available ?? '')
          .trim()
          .toUpperCase();
        const leadDaysRaw = chosenLocation?.availableDays;
        const leadDays = typeof leadDaysRaw === 'number' && Number.isFinite(leadDaysRaw) ? leadDaysRaw : null;
        if (entry?.productGid != null) {
          stockByProductId[String(entry.productGid)] = {
            inStock: normalized === 'Y' || normalized === 'L',
            leadDays: leadDays,
            foundAt: nearbyLocations[chosenIndex] || null,
          };
        }
      }
    } catch (error) {
      console.error('AI agent stock check failed:', error);
    }
    for (const card of cards) {
      const rawId = this.#stripGid(card.product?.productId);
      const known = rawId != null && Object.prototype.hasOwnProperty.call(stockByProductId, rawId);
      const {
        inStock: inStock,
        leadDays: leadDays,
        foundAt: foundAt,
      } = known
        ? stockByProductId[rawId]
        : {
            inStock: true,
            leadDays: null,
            foundAt: null,
          };
      card.button.disabled = false;
      if (inStock) {
        card.button.textContent = 'Add to Cart';
        card.button.classList.remove('btnEnquire');
        delete card.button.dataset.outOfStock;
      } else {
        card.button.textContent = 'Enquire Now';
        card.button.classList.add('btnEnquire');
        card.button.dataset.outOfStock = 'true';
      }
      if (card.stockEl) {
        card.stockEl.textContent = known ? this.#describeStockAvailability(inStock, leadDays, foundAt) : '';
        card.stockEl.classList.toggle('is-available', known && inStock);
        card.stockEl.classList.toggle('is-not-available', known && !inStock && !(leadDays > 0));
      }
      card.knownInStock = known ? inStock : null;
      card.knownLeadDays = known ? leadDays : null;
      card.knownFoundAt = known ? foundAt : null;
      if (card.splitProduct) {
        const splitRawId = this.#stripGid(card.splitProduct?.productId);
        const splitKnown = splitRawId != null && Object.prototype.hasOwnProperty.call(stockByProductId, splitRawId);
        const {
          inStock: splitInStock,
          leadDays: splitLeadDays,
          foundAt: splitFoundAt,
        } = splitKnown
          ? stockByProductId[splitRawId]
          : {
              inStock: true,
              leadDays: null,
              foundAt: null,
            };
        if (card.splitStockEl) {
          card.splitStockEl.textContent = splitKnown ? this.#describeStockAvailability(splitInStock, splitLeadDays, splitFoundAt) : '';
          card.splitStockEl.classList.toggle('is-available', splitKnown && splitInStock);
          card.splitStockEl.classList.toggle('is-not-available', splitKnown && !splitInStock && !(splitLeadDays > 0));
        }
        card.splitKnownInStock = splitKnown ? splitInStock : null;
        card.splitKnownLeadDays = splitKnown ? splitLeadDays : null;
        card.splitKnownFoundAt = splitKnown ? splitFoundAt : null;
      }
    }
    const gridCards = cards.filter((card) => card.cardEl);
    let allDisqualified = false;
    if (gridCards.length) {
      const container = this.refs.productsCarousel;
      const disqualifies = (card) => {
        const genuinelyUnobtainable = card.knownInStock === false && !(card.knownLeadDays > 0);
        if (genuinelyUnobtainable) return true;
        if (availabilityFilter == null) return false;
        if (card.knownLeadDays != null) return card.knownLeadDays > availabilityFilter;
        return !card.knownInStock;
      };
      const disqualified = gridCards.filter(disqualifies);
      const qualified = gridCards.filter((card) => !disqualifies(card));
      for (const card of [...qualified, ...disqualified]) container.appendChild(card.cardEl);
      allDisqualified = disqualified.length > 0 && qualified.length === 0;
      if (allDisqualified) {
        const note = document.createElement('p');
        note.className = 'agent-overlay__products-note';
        note.textContent =
          availabilityFilter != null
            ? 'Nothing matched that availability window at your selected store - shown below for reference.'
            : 'None of these are actually available at your selected store right now - shown below for reference.';
        container.parentElement.insertBefore(note, container);
      }
    }
    const knownCards = cards.filter((card) => card.knownInStock != null);
    if (knownCards.length) {
      const summary = knownCards
        .map((card) => {
          const desc = this.#describeStockAvailability(card.knownInStock, card.knownLeadDays, card.knownFoundAt);
          const atDifferentStore = card.knownInStock && card.knownFoundAt && card.knownFoundAt.id !== nearestLocationId;
          const switchNote = atDifferentStore ? ` [this is a DIFFERENT store than the customer's currently selected one - if they confirm buying this, tell them you're switching their selected store to ${card.knownFoundAt.name || 'that store'} first, THEN call add_to_cart]` : '';
          const splitNote =
            card.splitProduct && card.splitKnownInStock != null
              ? ` (front: ${card.product?.title || 'item'} - ${desc}; rear: ${card.splitProduct?.title || 'item'} - ${this.#describeStockAvailability(card.splitKnownInStock, card.splitKnownLeadDays, card.splitKnownFoundAt)})`
              : '';
          return `${card.product?.title || card.product?.handle || 'item'}: ${desc}${switchNote}${splitNote}`;
        })
        .join('; ');
      const unobtainable = knownCards.filter((card) => card.knownInStock === false && !(card.knownLeadDays > 0));
      const doNotRecommendNote = unobtainable.length
        ? ` Do NOT recommend or mention these by name again for the rest of this conversation, even if you already have - they have no stock at any nearby store checked: ${unobtainable.map((card) => card.product?.title || card.product?.handle || 'item').join(', ')}. Only exception: the customer explicitly asks about one of them by name, or a different store gets selected.`
        : '';
      const marker = allDisqualified
        ? `[[STOCK_CHECK: ${summary} - none of these are actually obtainable at the customer's selected store${availabilityFilter != null ? ` within ${availabilityFilter} day(s)` : ''}; say so plainly and offer to check a different store${availabilityFilter != null ? ' or widen the window' : ''}.${doNotRecommendNote}]]`
        : `[[STOCK_CHECK: real current stock for what's now on screen - ${summary} - use this if the customer asks about stock/availability, don't guess.${doNotRecommendNote}]]`;
      // Only a genuinely proactive case (nothing shown is actually
      // obtainable) earns an unprompted spoken reply. Otherwise this is
      // background bookkeeping - the agent just needs the real numbers on
      // hand for whenever the customer actually asks - and the earlier
      // version of this call let the agent reply out loud anyway, which
      // looked like the chat answering in two disconnected batches for one
      // customer message (visible reply, pause, second unprompted reply).
      await this.#continueConversationWithContext(marker, allDisqualified ? {} : { suppressTextReply: true, silent: true });
    }
  }
  #describeStockAvailability(inStock, leadDays, foundAt) {
    const hasLeadDays = typeof leadDays === 'number' && Number.isFinite(leadDays) && leadDays >= 0;
    const distanceLabel = typeof foundAt?.distanceKm === 'number' ? ` (${foundAt.distanceKm}km away)` : '';
    const storeLabel = foundAt?.name ? `${foundAt.name}${distanceLabel}` : foundAt ? `a nearby store${distanceLabel}` : '';
    if (foundAt && inStock) {
      return `In stock at ${storeLabel}`;
    }
    if (inStock && (!hasLeadDays || leadDays === 0)) return 'In stock now';
    if (hasLeadDays && leadDays > 0) {
      const availableDate = new Date(Date.now() + leadDays * 24 * 60 * 60 * 1e3);
      const dateLabel = availableDate.toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
      });
      const dayLabel = leadDays === 1 ? 'tomorrow' : `${dateLabel} (${leadDays} days)`;
      const atStore = storeLabel ? ` at ${storeLabel}` : '';
      return inStock ? `Available ${dayLabel}${atStore}` : `Available to order${atStore} - by ${dateLabel}`;
    }
    return inStock ? 'In stock now' : 'Out of stock';
  }
  #goToEnquiryPage() {
    const url = typeof window.getEnquiryPageUrl === 'function' ? window.getEnquiryPageUrl() : `${String(window.shopifyConfig?.storeURL || window.location.origin || '').replace(/\/$/, '')}/pages/contact`;
    window.location.href = url;
  }
  #getCartId(cartTools) {
    const cartData = cartTools?.cartData?.value || cartTools?.cartData || null;
    if (cartData?.id) return cartData.id;
    const raw = this.#safeLocalStorageGet('cartData');
    if (!raw) return null;
    try {
      return JSON.parse(raw)?.id || null;
    } catch {
      return null;
    }
  }
  #stripGid(gid) {
    if (typeof gid !== 'string' || !gid) return null;
    return gid.includes('/') ? gid.split('/').pop() : gid;
  }
  #safeLocalStorageGet(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }
  #formatTimestamp(date) {
    return new Intl.DateTimeFormat(undefined, {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  }
  #saveState() {
    try {
      const messageEls = [...this.refs.messages.children].filter((el) => el.classList.contains('agent-overlay__message') && !el.classList.contains('agent-overlay__message--typing') && !el.classList.contains('agent-overlay__message--tip'));
      const messages = messageEls.map((el) => ({
        role: el.classList.contains('agent-overlay__message--user') ? 'user' : 'agent',
        text: el.querySelector('.agent-overlay__message-text')?.textContent || '',
        timestamp: el.dataset.timestamp || new Date().toISOString(),
        variant: el.classList.contains('agent-overlay__message--disclaimer') ? 'disclaimer' : null,
      }));
      sessionStorage.setItem(
        AgentOverlayComponent.#STORAGE_KEY,
        JSON.stringify({
          proxyUrl: this.#proxyUrl,
          chatId: this.#chatId,
          enquiryId: this.#currentEnquiryId,
          lastCustomerActivityAt: this.#lastCustomerActivityAt || null,
          customerSendCount: this.#customerSendCount,
          chatSessionHasRecaptchaToken: this.#chatSessionHasRecaptchaToken,
          regoLookupCount: this.#regoLookupCount,
          messages: messages,
          isOpen: this.refs.dialog.open,
          products: this.#lastProducts.length
            ? {
                view: this.#lastRenderView || 'grid',
                products: this.#lastProducts,
                requestedLabels: this.#lastRequestedLabels,
                filterSummary: this.#lastFilterSummary,
                pageFilters: this.#lastPageFilters,
                availabilityFilter: this.#lastAvailabilityFilter,
                searchedSize: this.#lastSearchedSize,
                sizeSignature: this.#lastRenderSizeSignature,
              }
            : null,
        }),
      );
    } catch (error) {
      console.warn('AI agent chat: failed to save conversation state.', error);
    }
  }
  #restoreState() {
    let saved;
    try {
      const raw = sessionStorage.getItem(AgentOverlayComponent.#STORAGE_KEY);
      if (!raw) return;
      saved = JSON.parse(raw);
    } catch {
      return;
    }
    if (!saved || saved.proxyUrl !== this.#proxyUrl || !Array.isArray(saved.messages) || !saved.messages.length) {
      return;
    }
    this.#chatId = typeof saved.chatId === 'string' ? saved.chatId : null;
    this.#currentEnquiryId = typeof saved.enquiryId === 'string' ? saved.enquiryId : null;
    this.#lastCustomerActivityAt = typeof saved.lastCustomerActivityAt === 'number' ? saved.lastCustomerActivityAt : 0;
    this.#customerSendCount = typeof saved.customerSendCount === 'number' ? saved.customerSendCount : 0;
    this.#chatSessionHasRecaptchaToken = typeof saved.chatSessionHasRecaptchaToken === 'boolean' ? saved.chatSessionHasRecaptchaToken : null;
    this.#regoLookupCount = typeof saved.regoLookupCount === 'number' ? saved.regoLookupCount : 0;
    this.#hasGreeted = true;
    for (const message of saved.messages) {
      if (message && (message.role === 'user' || message.role === 'agent') && typeof message.text === 'string') {
        const timestamp = message.timestamp ? new Date(message.timestamp) : new Date();
        this.#addMessage(message.role, message.text, timestamp, message.variant === 'disclaimer' ? 'disclaimer' : null);
      }
    }
    this.#setNarrow(true);
    const savedProducts = saved.products;
    if (savedProducts && Array.isArray(savedProducts.products) && savedProducts.products.length) {
      const currentSignature = this.#getCurrentVehicleSizeSignature();
      const savedSignature = typeof savedProducts.sizeSignature === 'string' ? savedProducts.sizeSignature : null;
      if (savedSignature === null || savedSignature === currentSignature) {
        if (savedProducts.view === 'compare') {
          this.#renderCompare(savedProducts.products, savedProducts.requestedLabels);
        } else {
          this.#renderProducts(savedProducts.products, savedProducts.filterSummary, savedProducts.pageFilters, savedProducts.requestedLabels, savedProducts.availabilityFilter, savedProducts.searchedSize);
        }
        this.#lastRenderSizeSignature = currentSignature;
      }
    }
    if (saved.isOpen) {
      this.showDialog();
      if (this.#currentEnquiryId) this.#startEnquiryHeartbeat();
    }
  }
  #setNarrow(narrow) {
    this.refs.dialog.classList.toggle('agent-overlay__dialog--narrow', narrow);
  }
}

if (!customElements.get('agent-overlay-component')) {
  customElements.define('agent-overlay-component', AgentOverlayComponent);
}
